// Система локализации для игры
export type Language = 'ru' | 'en' | 'es' | 'fr' | 'de' | 'zh';

export interface Translations {
  // Общие
  common: {
    play: string;
    newGame: string;
    settings: string;
    back: string;
    next: string;
    close: string;
    confirm: string;
    cancel: string;
    yes: string;
    no: string;
    backToMenu: string;
    name: string;
    stamina: string;
    build?: string;
  };

  // Меню
  menu: {
    play: string;
    newGame: string;
    settings: string;
    newGameConfirm: string;
  };

  // Настройки
  settings: {
    title: string;
    language: string;
    selectLanguage: string;
    music: string;
    musicEnabled: string;
    musicDisabled: string;
    volume: string;
    enableMusic: string;
    disableMusic: string;
  };

  // Игра
  game: {
    turn: string;
    enemyTurn: string;
    yourUnits: string;
    enemyUnits: string;
    grid: string;
    gridOn: string;
    gridOff: string;
    queue: string;
    log: string;
    center: string;
    stamina: string;
    health: string;
    power: string;
    defense: string;
    endTurn: string;
    shield: string;
    removeShield: string;
    retreat: string;
    undo: string;
    redPotion: string;
    redPotionTooltip: string;
    clickBaseHint: string;
    loadingWorld: string;

    zoomIn: string;
    zoomOut: string;
    centerOnFighter: string;
  };

  // База
  base: {
    title: string;
    citadel: string;
    resources: string;
    gold: string;
    metal: string;
    wood: string;
    leather: string;
    crystals: string;
    liberation: string;
    buildings: string;
    fighters: string;
    barracks: string;
    forge: string;
    temple: string;
    mageTower: string;
    raid: string;
    castle: string;
    taxHouse: string;
    orcAttack: string;
    attackAlert: string;
    greenPotion: string;
    redPotion: string;
    buildHint?: string;
  };

  // Бой
  battle: {
    victory: string;
    defeat: string;
    retreat: string;
    continue: string;
    exit: string;
    baseDefended: string;
    raidCompleted: string;
    waveCompleted: string;
    defenseReward: string;
    waveTrophies: string;
    gold: string;
    metal: string;
    wood: string;
    leather: string;
    crystals: string;
    liberation: string;
    squadStatus: string;
    killed: string;
    continueBattle: string;
    takeAndRetreat: string;
    returnToBase: string;
    you: string;
    them: string;
    queue: string;
    log: string;
  };

  // Меню выбора бойцов
  fighterMenu?: {
    title: string;
    yourFighters: string;
    addFighter: string;
    addTestFighters: string;
    leftHand: string;
    rightHand: string;
    empty: string;
    attack: string;
    defense: string;
    range: string;
    actionPoints: string;
    details: string;
    health: string;
    mapSize: string;
    startGame: string;
    replaceConfirm: string;
    incompatibleWeapon: string;
    addAtLeastOne: string;
    fighter: string;
  };

  // Оружие и бойцы
  weapons: {
    club: string;
    small_sword: string;
    large_sword: string;
    small_shield: string;
    large_shield: string;
    small_bow: string;
    large_bow: string;
    wooden_shield: string;
    leather_armor: string;
    metal_armor: string;
    bone_dagger: string;
    rusty_axe: string;
    stone_mace: string;
    bone_shield_orc: string;
    wooden_shield_orc: string;
    stone_shield: string;
    throwing_tomahawk: string;
    orc_spear: string;
    mutant_leather: string;
    bone_armor: string;
  };
  fighters: {
    recruit: string;
    fighter: string;
    veteran: string;
  };
  enemies: {
    bone_cutter: string;
    bone_killer: string;
    axe_shield: string;
    mutant_dagger: string;
    axe_wielder: string;
    bone_axe_wielder: string;
    thrower: string;
    stone_destroyer: string;
    spearman: string;
  };
  buildings: Record<string, string>;

  // Модальные окна
  modals: {
    forge: {
      title: string;
      notBuilt: string;
      build: string;
      buildCost: string;
      categories: {
        melee: string;
        shields: string;
        armor: string;
        ranged: string;
      };
      buy: string;
      cost: string;
      rank: string;
      inventory: string;
      attack: string;
      defenseActive: string;
      defensePassive: string;
      actionPoints: string;
      range: string;
      weight: string;
      stock: string;
      emptyCategory: string;
      hoverHint: string;
    };
    barracks: {
      title: string;
      notBuilt: string;
      build: string;
      buildCost: string;
      recruit: string;
      recruitCost: string;
      equipment: string;
      newRecruit: string;
      leftHand: string;
      rightHand: string;
      armor: string;
      remove: string;
      ready: string;
      squad: string;
      count: string;
      empty: string;
      rank: string;
      progress: string;
      dead: string;
      health: string;
      stamina: string;
      rankRequired: string;
      statsUpdate: string;
      save: string;
      cancel: string;
      weaponStats: string;
      healthStamina: string;
      weaponStorage: string;
      ranks: string;
      finishRecruitment: string;
      attack: string;
      actionPoints: string;
      range: string;
      defense: string;
      weight: string;
      twoHanded: string;
    };
    raid: {
      title: string;
      selectDepth: string;
      depth: string;
      help: string;
      enemies: string;
      reward: string;
      selectFighters: string;
      startRaid: string;
      selected: string;
      maxFighters: string;
      health: string;
      helpTitle: string;
      helpDescription: string;
      stamina: string;
      attack: string;
      attackBlocked: string;
      defense: string;
      passive: string;
      weapons: string;
      progress: string;
      shields: string;
      armor: string;
    };
    temple: {
      title: string;
      notBuilt: string;
      build: string;
      buildCost: string;
      healAll: string;
      healDescription: string;
    };
    mageTower: {
      title: string;
      notBuilt: string;
      build: string;
      buildCost: string;
      potions: string;
      greenPotion: string;
      greenPotionDesc: string;
      greenPotionEffect: string;
      redPotion: string;
      redPotionDesc: string;
      redPotionEffect: string;
      stock: string;
      buy: string;
    };
    castle: {
      title: string;
      treasury: string;
      loyalty: string;
      army: string;
      conquestDiary: string;
      depth: string;
      description: string;
    };
    taxHouse: {
      title: string;
      description: string;
      taxSize: string;
      liberationRange: string;
      currentLiberation: string;
      currentTax: string;
      collectTaxes: string;
      collectTaxesCooldown: string;
      collectTaxesAmount: string;
    };
    orcAttack: {
      title: string;
      description: string;
      currentAttack: string;
      liberationPercent: string;
      orcCount: string;
      composition: string;
      startDefense: string;
      noFighters: string;
      attackIn: string;
      attackReady: string;
      attackTable: string;
      range1: string;
      range2: string;
      range3: string;
      range4: string;
      range5: string;
      range6: string;
      range7: string;
      range8: string;
      range9: string;
      range10: string;
      defense: string;
      maxFighters: string;
      mapSize: string;
      attackFrequency: string;
      warning: string;
      lossPenalty: string;
      youngOrc: string;
      fighterOrc: string;
      veteranOrc: string;
      cells: string;
      hours: string;
      minutes: string;
      range1Desc: string;
      range2Desc: string;
      range3Desc: string;
      range4Desc: string;
      range5Desc: string;
      range6Desc: string;
      range7Desc: string;
      range8Desc: string;
      range9Desc: string;
      range10Desc: string;
    };
  };

  // Туториалы
  tutorial: {
    next: string;
    understood: string;
    skip: string;
    skipTutorial: string;
    intro: {
      step1: string;
      step2: string;
      step3: string;
      step4: string;
      step5: string;
      step6: string;
    };
    base: {
      barracks: { title: string; description: string };
      raid: { title: string; description: string };
      forge: { title: string; description: string };
      temple: { title: string; description: string };
      mageTower: { title: string; description: string };
      castle: { title: string; description: string };
      taxHouse: { title: string; description: string };
      orcAttack: { title: string; description: string };
      all: { title: string; description: string };
    };
    extended: {
      resources: {
        liberation: { title: string; description: string };
        gold: { title: string; description: string };
        metal: { title: string; description: string };
        wood: { title: string; description: string };
        leather: { title: string; description: string };
        crystals: { title: string; description: string };
        fighters: { title: string; description: string };
        potions: { title: string; description: string };
      };
      taxHouse: {
        icon: { title: string; description: string };
        collect: { title: string; description: string };
      };
      barracks: {
        icon: { title: string; description: string };
        build: { title: string; description: string };
        recruit: { title: string; description: string };
        equip: { title: string; description: string };
        weaponStats?: { title: string; description: string };
        healthStamina?: { title: string; description: string };
        weaponStorage?: { title: string; description: string };
        ranks?: { title: string; description: string };
        finishRecruitment?: { title: string; description: string };
      };
      raid: {
        icon: { title: string; description: string };
        select: { title: string; description: string };
        start: { title: string; description: string };
      };
      battle: {
        unit: { title: string; description: string };
        movement: { title: string; description: string };
        obstacles: { title: string; description: string };
        attack: { title: string; description: string };
        defense: { title: string; description: string };
        turn: { title: string; description: string };
        queue: { title: string; description: string };
        end: { title: string; description: string };
        overview?: { title: string; description: string };
        fighterIndicators?: { title: string; description: string };
        currentFighterInfo?: { title: string; description: string };
        unitCounts?: { title: string; description: string };
        turnQueue?: { title: string; description: string };
        potions?: { title: string; description: string };
        undo?: { title: string; description: string };
        controls?: { title: string; description: string };
        obstacles_custom?: {
          tree: { title: string; description: string };
          snag: { title: string; description: string };
          bush: { title: string; description: string };
        };
      };
    };
  };
}

const translations: Record<Language, Translations> = {
  ru: {
    common: {
      play: 'ИГРАТЬ',
      newGame: 'НАЧАТЬ ЗАНОВО',
      settings: 'НАСТРОЙКИ',
      back: 'Назад',
      next: 'Далее',
      close: 'Закрыть',
      confirm: 'Подтвердить',
      cancel: 'Отмена',
      yes: 'Да',
      no: 'Нет',
      backToMenu: 'В главное меню',
      name: 'Имя',
      stamina: 'Стамина',
      build: 'Построить',
    },
    menu: {
      play: 'ИГРАТЬ',
      newGame: 'НАЧАТЬ ЗАНОВО',
      settings: 'НАСТРОЙКИ',
      newGameConfirm: 'Вы уверены, что хотите начать заново? Весь прогресс будет потерян.',
    },
    settings: {
      title: 'Настройки',
      language: 'Язык',
      selectLanguage: 'Выберите язык',
      music: 'Музыка',
      musicEnabled: 'Включена',
      musicDisabled: 'Выключена',
      volume: 'Громкость',
      enableMusic: 'Включить музыку',
      disableMusic: 'Выключить музыку',
    },
    game: {
      turn: 'ХОД',
      enemyTurn: 'Ход противника...',
      yourUnits: 'ВЫ',
      enemyUnits: 'ОНИ',
      grid: 'Сетка',
      gridOn: 'ВКЛ',
      gridOff: 'ВЫКЛ',
      queue: 'Очередь',
      log: 'Лог',
      center: '🎯',
      stamina: 'Сил',
      health: 'ХП',
      power: 'Сила',
      defense: 'Защита',
      endTurn: 'ХОД',
      shield: 'ЩИТ',
      removeShield: 'СНЯТЬ',
      retreat: 'ОТСТУПИТЬ',
      undo: 'ОТМЕНИТЬ',
      redPotion: '❤️',
      redPotionTooltip: 'Красное зелье - полное исцеление',
      clickBaseHint: 'НАЖМИТЕ НА КРЕПОСТЬ ДЛЯ ВХОДА',
      loadingWorld: 'ЗАГРУЗКА МИРА...',

      zoomIn: 'Приблизить',
      zoomOut: 'Отдалить',
      centerOnFighter: 'Центрировать',
    },
    base: {
      title: 'База',
      citadel: 'ЦИТАДЕЛЬ ЛЮДЕЙ',
      resources: 'Ресурсы',
      gold: 'Золото',
      metal: 'Металл',
      wood: 'Дерево',
      leather: 'Кожа',
      crystals: 'Кристаллы',
      liberation: 'Освобождение',
      buildings: 'Здания',
      fighters: 'Бойцы',
      barracks: 'Казармы',
      forge: 'Кузница',
      temple: 'Храм',
      mageTower: 'Башня мага',
      raid: 'Рейд',
      castle: 'Замок',
      taxHouse: 'Дом',
      orcAttack: 'Нападение орков',
      attackAlert: 'НАПАДЕНИЕ!',
      greenPotion: 'Зелёное зелье',
      redPotion: 'Красное зелье',
      buildHint: 'Нажмите на золотой замок здания, чтобы войти в него',
    },
    battle: {
      victory: 'Победа!',
      defeat: 'Поражение',
      retreat: 'Отступление',
      continue: 'Продолжить',
      exit: 'Выход',
      baseDefended: 'База защищена!',
      raidCompleted: 'Рейд Завершен!',
      waveCompleted: 'Волна {wave} пройдена!',
      defenseReward: 'Награда за защиту:',
      waveTrophies: 'Трофеи волны {wave}:',
      gold: 'Золото',
      metal: 'Металл',
      wood: 'Дерево',
      leather: 'Кожа',
      crystals: 'Кристаллы',
      liberation: 'Освобождение',
      squadStatus: 'Состояние отряда:',
      killed: 'ПОГИБ',
      continueBattle: 'В БОЙ (ВОЛНА {wave})',
      takeAndRetreat: 'ЗАБРАТЬ И ОТСТУПИТЬ',
      returnToBase: 'ВЕРНУТЬСЯ НА БАЗУ',
      you: 'ВЫ:',
      them: 'ОНИ:',
      queue: 'Очередь',
      log: 'Лог',
    },
    weapons: {
      club: 'Дубина',
      small_sword: 'Меч малый',
      large_sword: 'Меч большой',
      small_shield: 'Щит малый',
      large_shield: 'Щит большой',
      small_bow: 'Лук малый',
      large_bow: 'Лук большой',
      wooden_shield: 'Щит деревянный',
      leather_armor: 'Кожаные доспехи',
      metal_armor: 'Металлические доспехи',
      bone_dagger: 'Костяной Кинжал',
      rusty_axe: 'Ржавый Топор',
      stone_mace: 'Каменная Булава',
      bone_shield_orc: 'Костяной Щит Орка',
      wooden_shield_orc: 'Деревянный Щит Орка',
      stone_shield: 'Каменный Щит',
      throwing_tomahawk: 'Метательный Томагавк',
      orc_spear: 'Орочье Копьё',
      mutant_leather: 'Кожа Мутанта',
      bone_armor: 'Костяные Доспехи',
    },
    fighters: {
      recruit: 'Новобранец',
      fighter: 'Боец',
      veteran: 'Ветеран',
    },
    enemies: {
      bone_cutter: 'Костерез',
      bone_killer: 'Костяной убийца',
      axe_shield: 'Топор-щитовик',
      mutant_dagger: 'Мутант-кинжальщик',
      axe_wielder: 'Топорщик',
      bone_axe_wielder: 'Костяной топорщик',
      thrower: 'Метатель',
      stone_destroyer: 'Каменный разрушитель',
      spearman: 'Копейщик',
    },
    buildings: {},
    modals: {
      forge: {
        title: 'КУЗНИЦА',
        notBuilt: 'Кузница не построена',
        build: 'Построить',
        buildCost: '(50 Металла)',
        categories: {
          melee: 'Оружие',
          shields: 'Щиты',
          armor: 'Броня',
          ranged: 'Луки',
        },
        buy: 'КУПИТЬ',
        cost: 'Стоимость:',
        rank: 'Ранг',
        inventory: 'В инвентаре:',
        attack: 'АТК:',
        defenseActive: 'ЗАЩ (А):',
        defensePassive: 'ЗАЩ (П):',
        actionPoints: 'ОД:',
        range: 'ДИСТ:',
        weight: 'ВЕС:',
        stock: 'Запас:',
        emptyCategory: 'В этой категории нет предметов',
        hoverHint: 'Наведите на предмет для просмотра характеристик',
      },
      barracks: {
        title: 'КАЗАРМА',
        notBuilt: 'Казарма не построена',
        build: 'Построить',
        buildCost: '(50 Металла)',
        recruit: 'Нанять',
        recruitCost: '(20 Золота)',
        equipment: 'ЭКИПИРОВКА:',
        newRecruit: 'СНАРЯЖЕНИЕ НОВОБРАНЦА',
        leftHand: 'ЛЕВАЯ',
        rightHand: 'ПРАВАЯ',
        armor: 'БРОНЯ',
        remove: 'УБРАТЬ',
        // ready: 'ГОТОВ К БОЮ',
        statsUpdate: 'Хар-ки обновятся после набора',
        save: 'Сохранить изменения',
        cancel: 'ОТМЕНА',
        weaponStats: 'Характеристики оружия',
        healthStamina: 'Жизни и стамина',
        weaponStorage: 'Склад оружия',
        ranks: 'Ранги бойцов',
        finishRecruitment: 'Завершить набор',
        squad: 'Ваш отряд',
        count: 'Численность:',
        empty: 'Казарма пуста...',
        rank: 'Ранг',
        progress: 'Прогресс:',
        ready: 'ГОТОВ',
        dead: 'ПОГИБ',
        health: 'Здоровье',
        stamina: 'Выносливость',
        rankRequired: 'НУЖЕН РАНГ',
        attack: 'АТК:',
        actionPoints: 'ОД:',
        range: 'ДИСТ:',
        defense: 'ЗАЩ:',
        weight: 'ВЕС:',
        twoHanded: '2H',
      },
      raid: {
        title: 'РЕЙД',
        selectDepth: 'Выбери глубину рейда:',
        depth: 'ГЛУБИНА',
        help: 'Справка',
        enemies: 'Враги',
        reward: 'Награда:',
        selectFighters: 'Выбери бойцов',
        startRaid: 'В БОЙ',
        selected: 'Выбрано:',
        maxFighters: 'Макс. 5 бойцов',
        health: 'Здоровье',
        helpTitle: 'СПРАВКА: ГЛУБИНА {depth}',
        helpDescription: 'Возможные враги на этой глубине:',
        stamina: 'Стамина:',
        attack: 'Атака:',
        attackBlocked: 'Сначала отбейте нападение Орков',
        defense: 'Защита:',
        passive: 'пассивная',
        progress: 'Прогресс:',
        weapons: 'Оружие:',
        shields: 'Щиты:',
        armor: 'Броня:',
      },
      temple: {
        title: 'ХРАМ',
        notBuilt: 'Храм не построен',
        build: 'Построить',
        buildCost: '(100 Дерева)',
        healAll: 'ВЫЛЕЧИТЬ ВСЕХ',
        healDescription: '💚 Бесплатное лечение всех после рейда (1 раз/день)',
      },
      mageTower: {
        title: 'БАШНЯ МАГА',
        notBuilt: 'Башня мага не построена',
        build: 'Построить',
        buildCost: '(30 Кристаллов)',
        potions: '🧙‍♂️ ЗЕЛЬЯ:',
        greenPotion: 'Зелёное зелье',
        greenPotionDesc: 'Медленное лечение между боями',
        greenPotionEffect: 'Полностью восстанавливает здоровье выбранного юнита перед следующей волной рейда',
        redPotion: 'Красное зелье',
        redPotionDesc: 'Быстрое лечение в бою',
        redPotionEffect: 'Мгновенно полностью восстанавливает здоровье юнита во время его хода',
        stock: 'Запас:',
        buy: 'КУПИТЬ',
      },
      castle: {
        title: 'ГЛАВНЫЙ ЗАМОК',
        treasury: 'Королевская канцелярия',
        loyalty: 'Лояльность:',
        army: 'Армия:',
        conquestDiary: 'Дневник завоеваний:',
        depth: 'ГЛ.',
        description: 'Здесь куется история вашего королевства. Каждая победа приближает день полной свободы.',
      },
      taxHouse: {
        title: 'ДОМ СТАРОСТЫ',
        description: 'Это дом старосты, здесь собираются налоги с окрестных деревень.',
        taxSize: 'Размер налога:',
        liberationRange: 'освобождения:',
        currentLiberation: 'Текущий процент освобождения:',
        currentTax: 'Текущий размер налога:',
        collectTaxes: 'Собрать налоги',
        collectTaxesCooldown: 'Собрать налоги',
        collectTaxesAmount: 'Собрать налоги',
      },
      orcAttack: {
        title: 'НАПАДЕНИЕ ОРКОВ',
        description: 'Каждый час на базу нападают орки. Количество и состав зависят от процента освобождения.',
        currentAttack: 'Текущее нападение:',
        liberationPercent: 'Процент освобождения:',
        orcCount: 'Количество орков:',
        composition: 'Состав:',
        startDefense: 'НАЧАТЬ ЗАЩИТУ',
        noFighters: 'Нет доступных бойцов для защиты!',
        attackIn: 'Нападение через',
        attackReady: 'НАПАДЕНИЕ ГОТОВО!',
        attackTable: 'Таблица нападений:',
        range1: '0-10% освобождения',
        range2: '10-20% освобождения',
        range3: '20-30% освобождения',
        range4: '30-40% освобождения',
        range5: '40-50% освобождения',
        range6: '50-60% освобождения',
        range7: '60-70% освобождения',
        range8: '70-80% освобождения',
        range9: '80-90% освобождения',
        range10: '90-100% освобождения',
        defense: 'Защита:',
        maxFighters: 'Максимум 15 бойцов могут выйти на защиту',
        mapSize: 'Размер карты: 15x15 клеток',
        attackFrequency: 'Нападение происходит каждый час',
        warning: '⚠️ Внимание:',
        lossPenalty: 'При проигрыше в нападении на базу теряется 20% очков освобождения',
        youngOrc: 'молодых орка',
        fighterOrc: 'бойца',
        veteranOrc: 'ветерана',
        cells: 'клеток',
        hours: 'часов',
        minutes: 'минут',
        range1Desc: '3 молодых орка',
        range2Desc: '4 орка (3 молодых + 1 боец)',
        range3Desc: '5 орков (2 молодых + 2 бойца + 1 ветеран)',
        range4Desc: '6 орков (2 молодых + 2 бойца + 2 ветерана)',
        range5Desc: '7 орков (1 молодой + 3 бойца + 3 ветерана)',
        range6Desc: '8 орков (1 молодой + 3 бойца + 4 ветерана)',
        range7Desc: '9 орков (4 бойца + 5 ветеранов)',
        range8Desc: '10 орков (3 бойца + 7 ветеранов)',
        range9Desc: '10 орков (2 бойца + 8 ветеранов)',
        range10Desc: '10 орков (1 боец + 9 ветеранов)',
      },
    },
    tutorial: {
      next: 'Далее',
      understood: 'Понял, укрепляю базу!',
      skip: 'Пропустить',
      skipTutorial: 'Пропустить обучение',
      intro: {
        step1: 'Капитан... Вы очнулись. Порча... она пожрала всё.',
        step2: 'Три дня назад культисты открыли Врата Бездны.\nЧёрная слизь мутировала наших братьев — людей в орков,\nгномов в троллей, эльфов в пауков.',
        step3: 'Теперь они ломятся в наш последний оплот на равнинах.\nМы — всё, что осталось от королевства Эридан.',
        step4: 'Ты — Капитан Выживших. Твоя задача:\n• Строить базу — казармы, кузницу, храм\n• Нанимать и вооружать бойцов\n• Отбивать ночные нападения\n• Проводить рейды, отвоёвывая землю',
        step5: 'Каждый день Порча усиливается.\nЗаполни Очки Освобождения — разблокируй союзные биомы:\nлеса эльфов, горы гномов, болота дварфов.',
        step6: 'Если падёт наш оплот — конец. Закрой Врата Бездны... или умри вместе с нами.',
      },
      base: {
        barracks: { title: 'Казарма', description: 'Казарма нужна для набора новобранцев, так же здесь располагаются все наши войска.' },
        raid: { title: 'Ворота', description: 'Ворота, через них отправляются отряды наших доблестных воинов вглубь территории для освобождения наших земель от порождений скверны.' },
        forge: { title: 'Кузница', description: 'Кузница, здесь создаётся оружие и броня для наших воинов.' },
        temple: { title: 'Храм', description: 'Храм, здесь с божьего благословения наши воины могут излечить свои раны.' },
        mageTower: { title: 'Башня мага', description: 'Башня мага, здесь маги варят зелья жизни и выносливости для поддержания наших воинов.' },
        castle: { title: 'Замок', description: 'Замок, здесь пишется летопись этой эпохи.' },
        taxHouse: { title: 'Дом старосты', description: 'Дом старосты, здесь собираются налоги с окрестных деревень. Налоги можно собирать раз в 15 минут, размер налога зависит от процента освобождения территории.' },
        orcAttack: { title: 'Нападение орков', description: 'Каждый час на базу нападают орки. Количество и состав врагов зависят от процента освобождения - чем выше освобождение, тем сильнее и больше врагов. При проигрыше в нападении теряется 20% очков освобождения.' },
        all: { title: 'В путь!', description: 'Найми первых новобранцев и отправляйся в свой первый рейд, чтобы освободить королевство от захватчиков.' },
      },
      extended: {
        resources: {
          liberation: { title: 'Очки освобождения', description: 'Это процент освобождённой территории. Когда достигнете 100% - завершится демо-версия. В полной версии вас ждут цитадели Эльфов, Гномов, Номадов и Ящеролюдей!' },
          gold: { title: 'Золото', description: 'Основная валюта. Нужно для найма бойцов, постройки зданий и покупки оружия.' },
          metal: { title: 'Металл', description: 'Нужен для создания оружия и брони в кузнице.' },
          wood: { title: 'Дерево', description: 'Нужно для создания щитов и некоторых видов оружия в кузнице.' },
          leather: { title: 'Кожа', description: 'Нужна для создания доспехов в кузнице.' },
          crystals: { title: 'Кристаллы', description: 'Редкий ресурс. Нужны для покупки зелий в башне мага.' },
          fighters: { title: 'Бойцы', description: 'Количество ваших воинов. Формат: текущее/максимум. Максимум можно увеличить через постройки.' },
          potions: { title: 'Зелья', description: 'Зелёное зелье - медленное лечение между боями. Красное зелье - быстрое лечение в бою.' },
        },
        taxHouse: {
          icon: { title: 'Дом старосты', description: 'Нажмите на иконку дома, чтобы открыть окно сбора налогов.' },
          collect: { title: 'Собрать налоги', description: 'Нажмите кнопку "Собрать налоги", чтобы получить золото. Налоги можно собирать раз в 15 минут.' },
        },
        barracks: {
          icon: { title: 'Казарма', description: 'Нажмите на иконку казармы, чтобы открыть окно управления войсками.' },
          build: { title: 'Построить казарму', description: 'Сначала нужно построить казарму. Нажмите кнопку "Построить".' },
          recruit: { title: 'Нанять бойца', description: 'Теперь найдите бойца и нажмите кнопку "Нанять".' },
          equip: { title: 'Экипировать бойца', description: 'Выберите оружие и щит для бойца, затем нажмите "Экипировать".' },
          weaponStats: { title: 'Характеристики оружия', description: 'Здесь показан урон, дистанция и другие характеристики выбранного оружия.' },
          healthStamina: { title: 'Здоровье и Стамина', description: 'Зеленая полоска - здоровье, желтая - стамина. Стамина нужна для движения и атак.' },
          weaponStorage: { title: 'Склад оружия', description: 'Здесь хранится всё купленное и добытое оружие. Перетащите его в слот экипировки.' },
          ranks: { title: 'Ранги бойцов', description: 'Бойцы получают опыт и повышают ранг. С новым рангом открываются новые слоты экипировки.' },
          finishRecruitment: { title: 'Завершить наем', description: 'Когда боец экипирован, нажмите "Готово", чтобы добавить его в отряд.' },
        },
        raid: {
          icon: { title: 'Ворота', description: 'Нажмите на иконку ворот, чтобы открыть окно выбора рейда.' },
          select: { title: 'Выбрать рейд', description: 'Выберите глубину рейда и бойцов для участия.' },
          start: { title: 'Начать рейд', description: 'Нажмите кнопку "Начать рейд", чтобы отправиться в бой.' },
        },
        battle: {
          unit: { title: 'Информация о юните', description: 'Здесь отображается имя, стамина и здоровье текущего юнита.' },
          movement: { title: 'Движение', description: 'Кликните на клетку, чтобы переместить юнита. Синие клетки - доступны для движения.' },
          obstacles: { title: 'Препятствия', description: 'Дерево - нельзя пройти и стрелять. Каряга - нельзя пройти, но можно стрелять. Куст - можно пройти, но тратится 2 стамины.' },
          attack: { title: 'Атака', description: 'Кликните на врага, чтобы атаковать. Красные клетки - враги в зоне атаки.' },
          defense: { title: 'Защита', description: 'Нажмите кнопку "ЩИТ", чтобы активировать защиту. Это уменьшит получаемый урон.' },
          turn: { title: 'Ход', description: 'Нажмите кнопку "ХОД", чтобы завершить ход текущего юнита.' },
          queue: { title: 'Очередь', description: 'Здесь отображается очередь ходов всех юнитов.' },
          end: { title: 'Завершение', description: 'Поздравляем! Вы завершили обучение. Теперь вы готовы к бою!' },
          overview: { title: 'Поле боя', description: 'Это тактическая карта. Ваша цель - победить всех врагов и сохранить своих бойцов.' },
          fighterIndicators: { title: 'Индикаторы бойца', description: 'Над каждым бойцом есть полоски здоровья (зеленая) и стамины (желтая).' },
          currentFighterInfo: { title: 'Текущий боец', description: 'Слева показана детальная информация о бойце, чей сейчас ход.' },
          unitCounts: { title: 'Состав отрядов', description: 'Сверху показано количество живых бойцов в вашем отряде и у врага.' },
          turnQueue: { title: 'Очередь ходов', description: 'Здесь видно, кто будет ходить следующим. Планируйте свои действия заранее!' },
          potions: { title: 'Использование зелий', description: 'Нажмите на иконку зелья, чтобы вылечить текущего бойца. Зелья тратят очки действия.' },
          undo: { title: 'Отмена хода', description: 'Если вы ошиблись с движением, можно отменить последнее действие, пока ход не завершен.' },
          controls: { title: 'Управление камерой', description: 'Используйте колесико мыши для зума и перетаскивание (ПКМ) для перемещения карты.' },
          obstacles_custom: {
            tree: { title: 'Дерево', description: 'Полное препятствие. Нельзя пройти, нельзя прострелить.' },
            snag: { title: 'Коряга', description: 'Частичное укрытие. Нельзя пройти, но можно стрелять через него.' },
            bush: { title: 'Куст', description: 'Легкое препятствие. Можно пройти, но движение стоит дороже (2 стамины).' },
          },
        },
      },
    },
  },
  en: {
    common: {
      play: 'PLAY',
      newGame: 'NEW GAME',
      settings: 'SETTINGS',
      back: 'Back',
      next: 'Next',
      close: 'Close',
      confirm: 'Confirm',
      cancel: 'Cancel',
      yes: 'Yes',
      no: 'No',
      backToMenu: 'To Main Menu',
      name: 'Name',
      stamina: 'Stamina',
      build: 'Build',
    },
    menu: {
      play: 'PLAY',
      newGame: 'NEW GAME',
      settings: 'SETTINGS',
      newGameConfirm: 'Are you sure you want to start over? All progress will be lost.',
    },
    settings: {
      title: 'Settings',
      language: 'Language',
      selectLanguage: 'Select Language',
      music: 'Music',
      musicEnabled: 'Enabled',
      musicDisabled: 'Disabled',
      volume: 'Volume',
      enableMusic: 'Enable Music',
      disableMusic: 'Disable Music',
    },
    game: {
      turn: 'TURN',
      enemyTurn: 'Enemy turn...',
      yourUnits: 'YOU',
      enemyUnits: 'THEM',
      grid: 'Grid',
      gridOn: 'ON',
      gridOff: 'OFF',
      queue: 'Queue',
      log: 'Log',
      center: '🎯',
      stamina: 'Stamina',
      health: 'HP',
      power: 'Power',
      defense: 'Defense',
      endTurn: 'TURN',
      shield: 'SHIELD',
      removeShield: 'REMOVE',
      retreat: 'RETREAT',
      undo: 'UNDO',
      redPotion: '❤️',
      redPotionTooltip: 'Red Potion - Full Healing',
      clickBaseHint: 'CLICK ON THE CITADEL TO ENTER',
      loadingWorld: 'LOADING WORLD...',

      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      centerOnFighter: 'Center',
    },
    base: {
      title: 'Base',
      citadel: 'HUMAN CITADEL',
      resources: 'Resources',
      gold: 'Gold',
      metal: 'Metal',
      wood: 'Wood',
      leather: 'Leather',
      crystals: 'Crystals',
      liberation: 'Liberation',
      buildings: 'Buildings',
      fighters: 'Fighters',
      barracks: 'Barracks',
      forge: 'Forge',
      temple: 'Temple',
      mageTower: 'Mage Tower',
      raid: 'Raid',
      castle: 'Castle',
      taxHouse: 'House',
      orcAttack: 'Orc Attack',
      attackAlert: 'ATTACK!',
      greenPotion: 'Green Potion',
      redPotion: 'Red Potion',
      buildHint: 'Click on the golden castle icon to enter the building',
    },
    battle: {
      victory: 'Victory!',
      defeat: 'Defeat',
      retreat: 'Retreat',
      continue: 'Continue',
      exit: 'Exit',
      baseDefended: 'Base Defended!',
      raidCompleted: 'Raid Completed!',
      waveCompleted: 'Wave {wave} Completed!',
      defenseReward: 'Defense Reward:',
      waveTrophies: 'Wave {wave} Trophies:',
      gold: 'Gold',
      metal: 'Metal',
      wood: 'Wood',
      leather: 'Leather',
      crystals: 'Crystals',
      liberation: 'Liberation',
      squadStatus: 'Squad Status:',
      killed: 'KILLED',
      continueBattle: 'TO BATTLE (WAVE {wave})',
      takeAndRetreat: 'TAKE AND RETREAT',
      returnToBase: 'RETURN TO BASE',
      you: 'YOU:',
      them: 'THEM:',
      queue: 'Queue',
      log: 'Log',
    },
    modals: {
      forge: {
        title: 'FORGE',
        notBuilt: 'Forge not built',
        build: 'Build',
        buildCost: '(50 Metal)',
        categories: {
          melee: 'Weapons',
          shields: 'Shields',
          armor: 'Armor',
          ranged: 'Bows',
        },
        buy: 'BUY',
        cost: 'Cost:',
        rank: 'Rank',
        inventory: 'In inventory:',
        attack: 'ATK:',
        defenseActive: 'DEF (A):',
        defensePassive: 'DEF (P):',
        actionPoints: 'AP:',
        range: 'RANGE:',
        weight: 'WEIGHT:',
        stock: 'Stock:',
        emptyCategory: 'No items in this category',
        hoverHint: 'Hover over an item to view stats',
      },
      barracks: {
        title: 'BARRACKS',
        notBuilt: 'Barracks not built',
        build: 'Build',
        buildCost: '(50 Metal)',
        recruit: 'Recruit',
        recruitCost: '(20 Gold)',
        equipment: 'EQUIPMENT:',
        newRecruit: 'RECRUIT EQUIPMENT',
        leftHand: 'LEFT',
        rightHand: 'RIGHT',
        armor: 'ARMOR',
        remove: 'REMOVE',
        ready: 'READY',
        squad: 'Your squad',
        count: 'Count:',
        empty: 'Barracks is empty...',
        rank: 'Rank',
        progress: 'Progress:',
        dead: 'DEAD',
        health: 'Health',
        stamina: 'Stamina',
        rankRequired: 'RANK REQUIRED',
        statsUpdate: 'Stats will update after recruitment',
        save: 'Save changes',
        cancel: 'CANCEL',
        weaponStats: 'Weapon stats',
        healthStamina: 'Health and stamina',
        weaponStorage: 'Weapon storage',
        ranks: 'Fighter ranks',
        finishRecruitment: 'Finish recruitment',
        attack: 'ATK:',
        actionPoints: 'AP:',
        range: 'RANGE:',
        defense: 'DEF:',
        weight: 'WEIGHT:',
        twoHanded: '2H',
      },
      raid: {
        title: 'RAID',
        selectDepth: 'Select raid depth:',
        depth: 'DEPTH',
        help: 'Help',
        enemies: 'Enemies',
        reward: 'Reward:',
        selectFighters: 'Select fighters',
        startRaid: 'TO BATTLE',
        selected: 'Selected:',
        maxFighters: 'Max. 5 fighters',
        health: 'Health',
        helpTitle: 'HELP: DEPTH {depth}',
        helpDescription: 'Possible enemies at this depth:',
        stamina: 'Stamina:',
        attack: 'Attack:',
        attackBlocked: 'Repel the Orc attack first',
        defense: 'Defense:',
        passive: 'passive',
        weapons: 'Weapons:',
        progress: 'Progress:',
        shields: 'Shields:',
        armor: 'Armor:',
      },
      temple: {
        title: 'TEMPLE',
        notBuilt: 'Temple not built',
        build: 'Build',
        buildCost: '(100 Wood)',
        healAll: 'HEAL ALL',
        healDescription: '💚 Free full healing for all units once per day after a raid',
      },
      mageTower: {
        title: 'MAGE TOWER',
        notBuilt: 'Mage Tower not built',
        build: 'Build',
        buildCost: '(30 Crystals)',
        potions: '🧙‍♂️ POTIONS:',
        greenPotion: 'Green Potion',
        greenPotionDesc: 'Slow healing between battles',
        greenPotionEffect: 'Fully restores selected unit health before next raid wave',
        redPotion: 'Red Potion',
        redPotionDesc: 'Fast healing in battle',
        redPotionEffect: 'Instantly fully restores unit health during its turn',
        stock: 'Stock:',
        buy: 'BUY',
      },
      castle: {
        title: 'MAIN CASTLE',
        treasury: 'Royal Chancellery',
        loyalty: 'Loyalty:',
        army: 'Army:',
        conquestDiary: 'Conquest Diary:',
        depth: 'DEPTH',
        description: 'Here the history of your kingdom is forged. Every victory brings the day of full freedom closer.',
      },
      taxHouse: {
        title: 'TAX HOUSE',
        description: 'This is the tax house, where taxes are collected from nearby villages.',
        taxSize: 'Tax Size:',
        liberationRange: 'liberation:',
        currentLiberation: 'Current liberation percentage:',
        currentTax: 'Current tax size:',
        collectTaxes: 'Collect Taxes',
        collectTaxesCooldown: 'Collect Taxes',
        collectTaxesAmount: 'Collect Taxes',
      },
      orcAttack: {
        title: 'ORC ATTACK',
        description: 'Every hour orcs attack the base. The number and composition depend on the liberation percentage.',
        currentAttack: 'Current Attack:',
        liberationPercent: 'Liberation Percentage:',
        orcCount: 'Orc Count:',
        composition: 'Composition:',
        startDefense: 'START DEFENSE',
        noFighters: 'No available fighters for defense!',
        attackIn: 'Attack in',
        attackReady: 'ATTACK READY!',
        attackTable: 'Attack Table:',
        range1: '0-10% liberation',
        range2: '10-20% liberation',
        range3: '20-30% liberation',
        range4: '30-40% liberation',
        range5: '40-50% liberation',
        range6: '50-60% liberation',
        range7: '60-70% liberation',
        range8: '70-80% liberation',
        range9: '80-90% liberation',
        range10: '90-100% liberation',
        defense: 'Defense:',
        maxFighters: 'Maximum 15 fighters can go on defense',
        mapSize: 'Map size: 15x15 cells',
        attackFrequency: 'Attack occurs every hour',
        warning: '⚠️ Warning:',
        lossPenalty: 'If you lose the base attack, 20% of liberation points are lost',
        youngOrc: 'young orcs',
        fighterOrc: 'fighters',
        veteranOrc: 'veterans',
        cells: 'cells',
        hours: 'hours',
        minutes: 'minutes',
        range1Desc: '3 young orcs',
        range2Desc: '4 orcs (3 young + 1 fighter)',
        range3Desc: '5 orcs (2 young + 2 fighters + 1 veteran)',
        range4Desc: '6 orcs (2 young + 2 fighters + 2 veterans)',
        range5Desc: '7 orcs (1 young + 3 fighters + 3 veterans)',
        range6Desc: '8 orcs (1 young + 3 fighters + 4 veterans)',
        range7Desc: '9 orcs (4 fighters + 5 veterans)',
        range8Desc: '10 orcs (3 fighters + 7 veterans)',
        range9Desc: '10 orcs (2 fighters + 8 veterans)',
        range10Desc: '10 orcs (1 fighter + 9 veterans)',
      },
    },
    weapons: {
      club: 'Club',
      small_sword: 'Small Sword',
      large_sword: 'Large Sword',
      small_shield: 'Small Shield',
      large_shield: 'Large Shield',
      small_bow: 'Small Bow',
      large_bow: 'Large Bow',
      wooden_shield: 'Wooden Shield',
      leather_armor: 'Leather Armor',
      metal_armor: 'Metal Armor',
      bone_dagger: 'Bone Dagger',
      rusty_axe: 'Rusty Axe',
      stone_mace: 'Stone Mace',
      bone_shield_orc: 'Bone Orc Shield',
      wooden_shield_orc: 'Wooden Orc Shield',
      stone_shield: 'Stone Shield',
      throwing_tomahawk: 'Throwing Tomahawk',
      orc_spear: 'Orc Spear',
      mutant_leather: 'Mutant Leather',
      bone_armor: 'Bone Armor',
    },
    fighters: {
      recruit: 'Recruit',
      fighter: 'Fighter',
      veteran: 'Veteran',
    },
    enemies: {
      bone_cutter: 'Bone Cutter',
      bone_killer: 'Bone Killer',
      axe_shield: 'Axe-Shieldsman',
      mutant_dagger: 'Mutant Dagger-wielder',
      axe_wielder: 'Axe-wielder',
      bone_axe_wielder: 'Bone Axe-wielder',
      thrower: 'Thrower',
      stone_destroyer: 'Stone Destroyer',
      spearman: 'Spearman',
    },
    buildings: {},
    tutorial: {
      next: 'Next',
      understood: 'Understood, fortifying base!',
      skip: 'Skip',
      skipTutorial: 'Skip Tutorial',
      intro: {
        step1: 'Captain... You are awake. The Corruption... it has devoured everything.',
        step2: 'Three days ago, cultists opened the Void Gate.\nBlack slime mutated our brothers — humans into orcs,\ndwarves into trolls, elves into spiders.',
        step3: 'Now they are storming our last stronghold on the plains.\nWe are all that remains of the Kingdom of Eridan.',
        step4: 'You are the Survivor Captain. Your task:\n• Build the base — barracks, forge, temple\n• Recruit and arm fighters\n• Repel night attacks\n• Conduct raids to reclaim land',
        step5: 'Every day the Corruption grows stronger.\nFill Liberation Points — unlock allied biomes:\nelven forests, dwarven mountains, lizard swamps.',
        step6: 'If our stronghold falls — it is the end. Close the Void Gate... or die with us.',
      },
      base: {
        barracks: { title: 'Barracks', description: 'The Barracks is needed to recruit new soldiers, and it also houses all our troops.' },
        raid: { title: 'Gate', description: 'The Gate, through which squads of our brave warriors are sent deep into the territory to liberate our lands from the spawn of filth.' },
        forge: { title: 'Forge', description: 'The Forge, where weapons and armor are created for our warriors.' },
        temple: { title: 'Temple', description: 'The Temple, where with God\'s blessing our warriors can heal their wounds.' },
        mageTower: { title: 'Mage Tower', description: 'The Mage Tower, where mages brew health and stamina potions to support our warriors.' },
        castle: { title: 'Castle', description: 'The Castle, where the chronicle of this era is written.' },
        taxHouse: { title: 'Tax House', description: 'The Tax House, where taxes are collected from surrounding villages. Taxes can be collected once every 15 minutes, the tax amount depends on the percentage of territory liberation.' },
        orcAttack: { title: 'Orc Attack', description: 'Every hour orcs attack the base. The number and composition of enemies depend on the liberation percentage - the higher the liberation, the stronger and more numerous the enemies. Losing an attack result in losing 20% liberation points.' },
        all: { title: 'Let\'s Go!', description: 'Recruit your first rookies and go on your first raid to liberate the kingdom from the invaders.' },
      },
      extended: {
        resources: {
          liberation: { title: 'Liberation Points', description: 'This is the percentage of liberated territory. Reaching 100% completes the demo. The full version awaits with Citadels of Elves, Dwarves, Nomads, and Lizardmen!' },
          gold: { title: 'Gold', description: 'Main currency. Needed to recruit fighters, build structures, and buy weapons.' },
          metal: { title: 'Metal', description: 'Needed to craft weapons and armor in the forge.' },
          wood: { title: 'Wood', description: 'Needed to craft shields and some weapons in the forge.' },
          leather: { title: 'Leather', description: 'Needed to craft armor in the forge.' },
          crystals: { title: 'Crystals', description: 'Rare resource. Needed to buy potions in the mage tower.' },
          fighters: { title: 'Fighters', description: 'Number of your warriors. Format: current/max. Maximum can be increased through buildings.' },
          potions: { title: 'Potions', description: 'Green potion - slow healing between battles. Red potion - fast healing in battle.' },
        },
        taxHouse: {
          icon: { title: 'Tax House', description: 'Click the house icon to open the tax collection window.' },
          collect: { title: 'Collect Taxes', description: 'Click "Collect Taxes" to get gold. Taxes can be collected every 15 minutes.' },
        },
        barracks: {
          icon: { title: 'Barracks', description: 'Click the barracks icon to open the troop management window.' },
          build: { title: 'Build Barracks', description: 'First, you must build the barracks. Click the "Build" button.' },
          recruit: { title: 'Recruit Fighter', description: 'Now find a fighter and click the "Recruit" button.' },
          equip: { title: 'Equip Fighter', description: 'Select a weapon and shield for the fighter, then click "Equip".' },
          weaponStats: { title: 'Weapon Stats', description: 'Displays damage, range, and other stats of the selected weapon.' },
          healthStamina: { title: 'Health & Stamina', description: 'Green bar is health, yellow is stamina. Stamina is needed for movement and attacks.' },
          weaponStorage: { title: 'Weapon Storage', description: 'All bought and looted weapons are stored here. Drag them to an equipment slot.' },
          ranks: { title: 'Fighter Ranks', description: 'Fighters gain XP and rank up. New ranks unlock new equipment slots.' },
          finishRecruitment: { title: 'Finish Recruitment', description: 'When the fighter is equipped, click "Done" to add them to your squad.' },
        },
        raid: {
          icon: { title: 'Gate', description: 'Click the gate icon to open the raid selection window.' },
          select: { title: 'Select Raid', description: 'Choose raid depth and fighters to participate.' },
          start: { title: 'Start Raid', description: 'Click "Start Raid" to head into battle.' },
        },
        battle: {
          unit: { title: 'Unit Info', description: 'Displays name, stamina, and health of the current unit.' },
          movement: { title: 'Movement', description: 'Click a cell to move the unit. Blue cells are available for movement.' },
          obstacles: { title: 'Obstacles', description: 'Tree - cannot pass or shoot through. Snag - cannot pass but can shoot over. Bush - passable but costs 2 stamina.' },
          attack: { title: 'Attack', description: 'Click an enemy to attack. Red cells are enemies in range.' },
          defense: { title: 'Defense', description: 'Click "SHIELD" to activate defense. This reduces incoming damage.' },
          turn: { title: 'Turn', description: 'Click "TURN" to end the current unit\'s turn.' },
          queue: { title: 'Queue', description: 'Displays the turn order of all units.' },
          end: { title: 'Completion', description: 'Congratulations! You have completed the tutorial. Now you are ready for battle!' },
          overview: { title: 'Battlefield', description: 'This is the tactical map. Your goal is to defeat all enemies and preserve your fighters.' },
          fighterIndicators: { title: 'Fighter Indicators', description: 'Above each fighter are health (green) and stamina (yellow) bars.' },
          currentFighterInfo: { title: 'Current Fighter', description: 'Detailed info about the active fighter is shown on the left.' },
          unitCounts: { title: 'Squad Status', description: 'The top shows the number of living fighters in your squad vs the enemy.' },
          turnQueue: { title: 'Turn Queue', description: 'Shows who will act next. Plan your actions ahead! ' },
          potions: { title: 'Using Potions', description: 'Click a potion icon to heal the current fighter. Potions cost Action Points.' },
          undo: { title: 'Undo Action', description: 'Mistake? You can undo the last move if the turn hasn\'t ended.' },
          controls: { title: 'Camera Controls', description: 'Use mouse wheel to zoom and drag (RMB) to move the map.' },
          obstacles_custom: {
            tree: { title: 'Tree', description: 'Full cover. Cannot pass, cannot shoot through.' },
            snag: { title: 'Snag', description: 'Partial cover. Cannot pass, but can shoot over.' },
            bush: { title: 'Bush', description: 'Light obstacle. Passable, but movement costs more (2 stamina).' },
          },
        },
      },
    },
  },
  es: {
    common: {
      play: 'JUGAR',
      newGame: 'NUEVO JUEGO',
      settings: 'CONFIGURACIÓN',
      back: 'Atrás',
      next: 'Siguiente',
      close: 'Cerrar',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      yes: 'Sí',
      no: 'No',
      backToMenu: 'Al Menú Principal',
      name: 'Nombre',
      stamina: 'Resistencia',
    },
    menu: {
      play: 'JUGAR',
      newGame: 'NUEVO JUEGO',
      settings: 'CONFIGURACIÓN',
      newGameConfirm: '¿Estás seguro de que quieres empezar de nuevo? Todo el progreso se perderá.',
    },
    settings: {
      title: 'Configuración',
      language: 'Idioma',
      selectLanguage: 'Seleccionar Idioma',
      music: 'Música',
      musicEnabled: 'Activada',
      musicDisabled: 'Desactivada',
      volume: 'Volumen',
      enableMusic: 'Activar Música',
      disableMusic: 'Desactivar Música',
    },
    game: {
      turn: 'TURNO',
      enemyTurn: 'Turno del enemigo...',
      yourUnits: 'TÚ',
      enemyUnits: 'ELLOS',
      grid: 'Cuadrícula',
      gridOn: 'ON',
      gridOff: 'OFF',
      queue: 'Cola',
      log: 'Registro',
      center: '🎯',
      stamina: 'Resistencia',
      health: 'PV',
      power: 'Poder',
      defense: 'Defensa',
      endTurn: 'TURNO',
      shield: 'ESCUDO',
      removeShield: 'QUITAR',
      retreat: 'RETIRARSE',
      undo: 'DESHACER',
      redPotion: '❤️',
      redPotionTooltip: 'Poción Roja - Curación Completa',
      clickBaseHint: 'HAGA CLIC EN LA CIUDADELA PARA ENTRAR',
      loadingWorld: 'CARGANDO MUNDO...',

      zoomIn: 'Acercar',
      zoomOut: 'Alejar',
      centerOnFighter: 'Centrar',
    },
    base: {
      title: 'Base',
      citadel: 'CIUDADELA HUMANA',
      resources: 'Recursos',
      gold: 'Oro',
      metal: 'Metal',
      wood: 'Madera',
      leather: 'Cuero',
      crystals: 'Cristales',
      liberation: 'Liberación',
      buildings: 'Edificios',
      fighters: 'Luchadores',
      barracks: 'Cuartel',
      forge: 'Forja',
      temple: 'Templo',
      mageTower: 'Torre del Mago',
      raid: 'Incursión',
      castle: 'Castillo',
      taxHouse: 'Casa',
      orcAttack: 'Ataque Orco',
      attackAlert: '¡ATAQUE!',
      greenPotion: 'Poción Verde',
      redPotion: 'Poción Roja',
    },
    modals: {
      forge: {
        title: 'FORJA',
        notBuilt: 'Forja no construida',
        build: 'Construir',
        buildCost: '(50 Metal)',
        categories: {
          melee: 'Armas',
          shields: 'Escudos',
          armor: 'Armadura',
          ranged: 'Arcos',
        },
        buy: 'COMPRAR',
        cost: 'Costo:',
        rank: 'Rango',
        inventory: 'En inventario:',
        attack: 'ATQ:',
        defenseActive: 'DEF (A):',
        defensePassive: 'DEF (P):',
        actionPoints: 'PA:',
        range: 'ALC:',
        weight: 'PESO:',
        stock: 'Stock:',
        emptyCategory: 'No hay objetos en esta categoría',
        hoverHint: 'Pasa el cursor sobre un objeto para ver estadísticas',
      },
      castle: {
        title: 'CASTILLO PRINCIPAL',
        treasury: 'Cancillería Real',
        loyalty: 'Lealtad:',
        army: 'Ejército:',
        conquestDiary: 'Diario de Conquistas:',
        depth: 'PROF.',
        description: 'Aquí se forja la historia de tu reino. Cada victoria acerca el día de la libertad completa.',
      },
      taxHouse: {
        title: 'CASA DE IMPUESTOS',
        description: 'Esta es la casa de impuestos, donde se recaudan los impuestos de las aldeas cercanas.',
        taxSize: 'Tamaño del Impuesto:',
        liberationRange: 'liberación:',
        currentLiberation: 'Porcentaje de liberación actual:',
        currentTax: 'Tamaño del impuesto actual:',
        collectTaxes: 'Recaudar Impuestos',
        collectTaxesCooldown: 'Recaudar Impuestos',
        collectTaxesAmount: 'Recaudar Impuestos',
      },
      orcAttack: {
        title: 'ATAQUE ORCO',
        description: 'Cada hora los orcos atacan la base. El número y la composición dependen del porcentaje de liberación.',
        currentAttack: 'Ataque Actual:',
        liberationPercent: 'Porcentaje de Liberación:',
        orcCount: 'Cantidad de Orcos:',
        composition: 'Composición:',
        attackIn: 'Ataque en',
        attackReady: '¡ATAQUE LISTO!',
        attackTable: 'Tabla de Ataques:',
        range1: '0-10% liberación',
        range2: '10-20% liberación',
        range3: '20-30% liberación',
        range4: '30-40% liberación',
        range5: '40-50% liberación',
        range6: '50-60% liberación',
        range7: '60-70% liberación',
        range8: '70-80% liberación',
        range9: '80-90% liberación',
        range10: '90-100% liberación',
        startDefense: 'INICIAR DEFENSA',
        noFighters: '¡No hay luchadores disponibles para la defensa!',
        defense: 'Defensa:',
        maxFighters: 'Máximo 15 luchadores pueden salir a defender',
        mapSize: 'Tamaño del mapa: 15x15 celdas',
        attackFrequency: 'El ataque ocurre cada hora',
        warning: '⚠️ Atención:',
        lossPenalty: 'Si pierdes el ataque a la base, se pierde el 20% de los puntos de liberación',
        youngOrc: 'orcos jóvenes',
        fighterOrc: 'luchadores',
        veteranOrc: 'veteranos',
        cells: 'celdas',
        hours: 'horas',
        minutes: 'minutos',
        range1Desc: '3 orcos jóvenes',
        range2Desc: '4 orcos (3 jóvenes + 1 luchador)',
        range3Desc: '5 orcos (2 jóvenes + 2 luchadores + 1 veterano)',
        range4Desc: '6 orcos (2 jóvenes + 2 luchadores + 2 veteranos)',
        range5Desc: '7 orcos (1 joven + 3 luchadores + 3 veteranos)',
        range6Desc: '8 orcos (1 joven + 3 luchadores + 4 veteranos)',
        range7Desc: '9 orcos (4 luchadores + 5 veteranos)',
        range8Desc: '10 orcos (3 luchadores + 7 veteranos)',
        range9Desc: '10 orcos (2 luchadores + 8 veteranos)',
        range10Desc: '10 orcos (1 luchador + 9 veteranos)',
      },
      barracks: {
        title: 'CUARTEL',
        notBuilt: 'Cuartel no construido',
        build: 'Construir',
        buildCost: '(50 Metal)',
        recruit: 'Reclutar',
        recruitCost: '(20 Oro)',
        equipment: 'EQUIPO:',
        newRecruit: 'EQUIPO DE RECLUTA',
        leftHand: 'IZQUIERDA',
        rightHand: 'DERECHA',
        armor: 'ARMADURA',
        remove: 'QUITAR',
        // ready: 'LISTO PARA LA BATALLA',
        statsUpdate: 'Las estadísticas se actualizarán después del reclutamiento',
        save: 'Guardar cambios',
        cancel: 'CANCELAR',
        weaponStats: 'Estadísticas de arma',
        healthStamina: 'Salud y resistencia',
        weaponStorage: 'Almacén de armas',
        ranks: 'Rangos de luchadores',
        finishRecruitment: 'Terminar reclutamiento',
        squad: 'Tu escuadrón',
        count: 'Conteo:',
        empty: 'El cuartel está vacío...',
        rank: 'Rango',
        progress: 'Progreso:',
        ready: 'LISTO',
        dead: 'MUERTO',
        health: 'Salud',
        stamina: 'Resistencia',
        rankRequired: 'RANGO REQUERIDO',
        attack: 'ATQ:',
        actionPoints: 'PA:',
        range: 'ALC:',
        defense: 'DEF:',
        weight: 'PESO:',
        twoHanded: '2M',
      },
      raid: {
        title: 'INCURSIÓN',
        selectDepth: 'Selecciona la profundidad de la incursión:',
        depth: 'PROFUNDIDAD',
        help: 'Ayuda',
        enemies: 'Enemigos',
        reward: 'Recompensa:',
        selectFighters: 'Selecciona luchadores',
        startRaid: 'A LA BATALLA',
        selected: 'Seleccionado:',
        maxFighters: 'Máx. 5 luchadores',
        health: 'Salud',
        helpTitle: 'AYUDA: PROFUNDIDAD {depth}',
        helpDescription: 'Posibles enemigos en esta profundidad:',
        stamina: 'Resistencia:',
        attack: 'Ataque:',
        attackBlocked: 'Repele el ataque orco primero',
        defense: 'Defensa:',
        passive: 'pasiva',
        progress: 'Progreso:',
        weapons: 'Armas:',
        shields: 'Escudos:',
        armor: 'Armadura:',

      },
      temple: {
        title: 'TEMPLO',
        notBuilt: 'Templo no construido',
        build: 'Construir',
        buildCost: '(100 Madera)',
        healAll: 'CURAR A TODOS',
        healDescription: '💚 Sanación gratuita de todas las unidades una vez al día después de una incursión',
      },
      mageTower: {
        title: 'TORRE DEL MAGO',
        notBuilt: 'Torre del Mago no construida',
        build: 'Construir',
        buildCost: '(30 Cristales)',
        potions: '🧙‍♂️ POCIONES:',
        greenPotion: 'Poción Verde',
        greenPotionDesc: 'Curación lenta entre batallas',
        greenPotionEffect: 'Restaura completamente la salud de la unidad seleccionada antes de la siguiente onda de incursión',
        redPotion: 'Poción Roja',
        redPotionDesc: 'Curación rápida en batalla',
        redPotionEffect: 'Restaura instantáneamente la salud completa de la unidad durante su turno',
        stock: 'Stock:',
        buy: 'COMPRAR',
      },
    },
    battle: {
      victory: '¡Victoria!',
      defeat: 'Derrota',
      retreat: 'Retirada',
      continue: 'Continuar',
      exit: 'Salir',
      baseDefended: '¡Base Defendida!',
      raidCompleted: '¡Incursión Completada!',
      waveCompleted: '¡Onda {wave} Completada!',
      defenseReward: 'Recompensa por Defensa:',
      waveTrophies: 'Trofeos de Onda {wave}:',
      gold: 'Oro',
      metal: 'Metal',
      wood: 'Madera',
      leather: 'Cuero',
      crystals: 'Cristales',
      liberation: 'Liberación',
      squadStatus: 'Estado del Escuadrón:',
      killed: 'MUERTO',
      continueBattle: 'A LA BATALLA (ONDA {wave})',
      takeAndRetreat: 'TOMAR Y RETIRARSE',
      returnToBase: 'VOLVER A LA BASE',
      you: 'TÚ:',
      them: 'ELLOS:',
      queue: 'Cola',
      log: 'Registro',
    },
    fighterMenu: {
      title: 'Selección de Luchadores',
      yourFighters: 'Tus Luchadores',
      addFighter: '+ Agregar Luchador',
      addTestFighters: '⚡ Agregar Prueba (5)',
      leftHand: 'Mano Izquierda:',
      rightHand: 'Mano Derecha:',
      empty: 'Vacío',
      attack: 'Ataque:',
      defense: 'Defensa:',
      range: 'Alcance:',
      actionPoints: 'Puntos de Acción:',
      details: 'Detalles del Luchador',
      health: 'Salud:',
      mapSize: 'Tamaño del Mapa:',
      startGame: 'Comenzar Juego',
      replaceConfirm: 'Esto reemplazará a todos los luchadores actuales. ¿Continuar?',
      incompatibleWeapon: '¡Este arma es incompatible con el arma en la otra mano!',
      addAtLeastOne: '¡Agrega al menos un luchador!',
      fighter: 'Luchador',
    },
    weapons: {
      club: 'Club',
      small_sword: 'Small Sword',
      large_sword: 'Large Sword',
      small_shield: 'Small Shield',
      large_shield: 'Large Shield',
      small_bow: 'Small Bow',
      large_bow: 'Large Bow',
      wooden_shield: 'Wooden Shield',
      leather_armor: 'Leather Armor',
      metal_armor: 'Metal Armor',
      bone_dagger: 'Bone Dagger',
      rusty_axe: 'Rusty Axe',
      stone_mace: 'Stone Mace',
      bone_shield_orc: 'Bone Orc Shield',
      wooden_shield_orc: 'Wooden Orc Shield',
      stone_shield: 'Stone Shield',
      throwing_tomahawk: 'Throwing Tomahawk',
      orc_spear: 'Orc Spear',
      mutant_leather: 'Mutant Leather',
      bone_armor: 'Bone Armor',
    },
    fighters: {
      recruit: 'Recruit',
      fighter: 'Fighter',
      veteran: 'Veteran',
    },
    enemies: {
      bone_cutter: 'Cortador de Huesos',
      bone_killer: 'Asesino de Huesos',
      axe_shield: 'Hacha-Escudo',
      mutant_dagger: 'Mutante Daga',
      axe_wielder: 'Portador de Hacha',
      bone_axe_wielder: 'Portador de Hacha de Hueso',
      thrower: 'Lanzador',
      stone_destroyer: 'Destructor de Piedra',
      spearman: 'Lancero',
    },
    buildings: {},
    tutorial: {
      next: 'Далее',
      understood: 'Понял, укрепляю базу!',
      skip: 'Пропустить',
      skipTutorial: 'Saltar Tutorial',
      intro: {
        step1: 'Капитан... Вы очнулись. Порча... она пожрала всё.',
        step2: 'Три дня назад культисты открыли Врата Бездны.\nЧёрная слизь мутировала наших братьев — людей в орков,\nгномов в троллей, эльфов в пауков.',
        step3: 'Теперь они ломятся в наш последний оплот на равнинах.\nМы — всё, что осталось от королевства Эридан.',
        step4: 'Ты — Капитан Выживших. Твоя задача:\n• Строить базу — казармы, кузницу, храм\n• Нанимать и вооружать бойцов\n• Отбивать ночные нападения\n• Проводить рейды, отвоёвывая землю',
        step5: 'Каждый день Порча усиливается.\nЗаполни Очки Освобождения — разблокируй союзные биомы:\nлеса эльфов, горы гномов, болота дварфов.',
        step6: 'Если падёт наш оплот — конец. Закрой Врата Бездны... или умри вместе с нами.',
      },
      base: {
        barracks: { title: 'Казарма', description: 'Казарма нужна для набора новобранцев, так же здесь располагаются все наши войска.' },
        raid: { title: 'Ворота', description: 'Ворота, через них отправляются отряды наших доблестных воинов вглубь территории для освобождения наших земель от порождений скверны.' },
        forge: { title: 'Кузница', description: 'Кузница, здесь создаётся оружие и броня для наших воинов.' },
        temple: { title: 'Храм', description: 'Храм, здесь с божьего благословения наши воины могут излечить свои раны.' },
        mageTower: { title: 'Башня мага', description: 'Башня мага, здесь маги варят зелья жизни и выносливости для поддержания наших воинов.' },
        castle: { title: 'Замок', description: 'Замок, здесь пишется летопись этой эпохи.' },
        taxHouse: { title: 'Дом старосты', description: 'Дом старосты, здесь собираются налоги с окрестных деревень. Налоги можно собирать раз в 15 минут, размер налога зависит от процента освобождения территории.' },
        orcAttack: { title: 'Нападение орков', description: 'Каждый час на базу нападают орки. Количество и состав врагов зависят от процента освобождения - чем выше освобождение, тем сильнее и больше врагов. При проигрыше в нападении теряется 20% очков освобождения.' },
        all: { title: 'В путь!', description: 'Найми первых новобранцев и отправляйся в свой первый рейд, чтобы освободить королевство от захватчиков.' },
      },
      extended: {
        resources: {
          liberation: { title: 'Очки освобождения', description: 'Это процент освобождённой территории. Когда достигнете 100% - завершится демо-версия. В полной версии вас ждут цитадели Эльфов, Гномов, Номадов и Ящеролюдей!' },
          gold: { title: 'Золото', description: 'Основная валюта. Нужно для найма бойцов, постройки зданий и покупки оружия.' },
          metal: { title: 'Металл', description: 'Нужен для создания оружия и брони в кузнице.' },
          wood: { title: 'Дерево', description: 'Нужно для создания щитов и некоторых видов оружия в кузнице.' },
          leather: { title: 'Кожа', description: 'Нужна для создания доспехов в кузнице.' },
          crystals: { title: 'Кристаллы', description: 'Редкий ресурс. Нужны для покупки зелий в башне мага.' },
          fighters: { title: 'Бойцы', description: 'Количество ваших воинов. Формат: текущее/максимум. Максимум можно увеличить через постройки.' },
          potions: { title: 'Зелья', description: 'Зелёное зелье - медленное лечение между боями. Красное зелье - быстрое лечение в бою.' },
        },
        taxHouse: {
          icon: { title: 'Дом старосты', description: 'Нажмите на иконку дома, чтобы открыть окно сбора налогов.' },
          collect: { title: 'Собрать налоги', description: 'Нажмите кнопку "Собрать налоги", чтобы получить золото. Налоги можно собирать раз в 15 минут.' },
        },
        barracks: {
          icon: { title: 'Казарма', description: 'Нажмите на иконку казармы, чтобы открыть окно управления войсками.' },
          build: { title: 'Построить казарму', description: 'Сначала нужно построить казарму. Нажмите кнопку "Построить".' },
          recruit: { title: 'Нанять бойца', description: 'Теперь найдите бойца и нажмите кнопку "Нанять".' },
          equip: { title: 'Экипировать бойца', description: 'Выберите оружие и щит для бойца, затем нажмите "Экипировать".' },
        },
        raid: {
          icon: { title: 'Ворота', description: 'Нажмите на иконку ворот, чтобы открыть окно выбора рейда.' },
          select: { title: 'Выбрать рейд', description: 'Выберите глубину рейда и бойцов для участия.' },
          start: { title: 'Начать рейд', description: 'Нажмите кнопку "Начать рейд", чтобы отправиться в бой.' },
        },
        battle: {
          unit: { title: 'Информация о юните', description: 'Здесь отображается имя, стамина и здоровье текущего юнита.' },
          movement: { title: 'Движение', description: 'Кликните на клетку, чтобы переместить юнита. Синие клетки - доступны для движения.' },
          obstacles: { title: 'Препятствия', description: 'Дерево - нельзя пройти и стрелять. Каряга - нельзя пройти, но можно стрелять. Куст - можно пройти, но тратится 2 стамины.' },
          attack: { title: 'Атака', description: 'Кликните на врага, чтобы атаковать. Красные клетки - враги в зоне атаки.' },
          defense: { title: 'Защита', description: 'Нажмите кнопку "ЩИТ", чтобы активировать защиту. Это уменьшит получаемый урон.' },
          turn: { title: 'Ход', description: 'Нажмите кнопку "ХОД", чтобы завершить ход текущего юнита.' },
          queue: { title: 'Очередь', description: 'Здесь отображается очередь ходов всех юнитов.' },
          end: { title: 'Завершение', description: 'Поздравляем! Вы завершили обучение. Теперь вы готовы к бою!' },
        },
      },
    },
  },
  fr: {
    common: {
      play: 'JOUER',
      newGame: 'NOUVEAU JEU',
      settings: 'PARAMÈTRES',
      back: 'Retour',
      next: 'Suivant',
      close: 'Fermer',
      confirm: 'Confirmer',
      cancel: 'Annuler',
      yes: 'Oui',
      no: 'Non',
      backToMenu: 'Retour au Menu Principal',
      name: 'Nom',
      stamina: 'Endurance',
    },
    menu: {
      play: 'JOUER',
      newGame: 'NOUVEAU JEU',
      settings: 'PARAMÈTRES',
      newGameConfirm: 'Êtes-vous sûr de vouloir recommencer ? Tous les progrès seront perdus.',
    },
    settings: {
      title: 'Paramètres',
      language: 'Langue',
      selectLanguage: 'Sélectionner la langue',
      music: 'Musique',
      musicEnabled: 'Activée',
      musicDisabled: 'Désactivée',
      volume: 'Volume',
      enableMusic: 'Activer la Musique',
      disableMusic: 'Désactiver la Musique',
    },
    game: {
      turn: 'TOUR',
      enemyTurn: 'Tour de l\'ennemi...',
      yourUnits: 'VOUS',
      enemyUnits: 'EUX',
      grid: 'Grille',
      gridOn: 'ON',
      gridOff: 'OFF',
      queue: 'File',
      log: 'Journal',
      center: '🎯',
      stamina: 'Endurance',
      health: 'PV',
      power: 'Puissance',
      defense: 'Défense',
      endTurn: 'TOUR',
      shield: 'BOUCLIER',
      removeShield: 'RETIRER',
      retreat: 'RETRAITE',
      undo: 'ANNULER',
      redPotion: '❤️',
      redPotionTooltip: 'Potion Rouge - Guérison Complète',
      clickBaseHint: 'CLIQUEZ SUR LA CITADELLE POUR ENTRER',
      loadingWorld: 'CHARGEMENT DU MONDE...',
      zoomIn: 'Zoomer',
      zoomOut: 'Dézoomer',
      centerOnFighter: 'Centrer',
    },
    base: {
      title: 'Base',
      citadel: 'CITADELLE HUMAINE',
      resources: 'Ressources',
      gold: 'Or',
      metal: 'Métal',
      wood: 'Bois',
      leather: 'Cuir',
      crystals: 'Cristaux',
      liberation: 'Libération',
      buildings: 'Bâtiments',
      fighters: 'Combattants',
      barracks: 'Caserne',
      forge: 'Forge',
      temple: 'Temple',
      mageTower: 'Tour du Mage',
      raid: 'Raid',
      castle: 'Château',
      taxHouse: 'Maison',
      orcAttack: 'Attaque Orc',
      attackAlert: 'ATTAQUE !',
      greenPotion: 'Potion Verte',
      redPotion: 'Potion Rouge',
    },
    modals: {
      forge: {
        title: 'FORGE',
        notBuilt: 'Forge non construite',
        build: 'Construire',
        buildCost: '(50 Métal)',
        categories: {
          melee: 'Armes',
          shields: 'Boucliers',
          armor: 'Armure',
          ranged: 'Arcs',
        },
        buy: 'ACHETER',
        cost: 'Coût:',
        rank: 'Rang',
        inventory: 'En inventaire:',
        attack: 'ATQ:',
        defenseActive: 'DEF (A):',
        defensePassive: 'DEF (P):',
        actionPoints: 'PA:',
        range: 'PORT:',
        weight: 'POIDS:',
        stock: 'Stock:',
        emptyCategory: 'Aucun objet dans cette catégorie',
        hoverHint: 'Survolez un objet pour voir les statistiques',
      },
      castle: {
        title: 'CHÂTEAU PRINCIPAL',
        treasury: 'Chancellerie Royale',
        loyalty: 'Loyauté:',
        army: 'Armée:',
        conquestDiary: 'Journal de Conquêtes:',
        depth: 'PROF.',
        description: 'Ici se forge l\'histoire de votre royaume. Chaque victoire rapproche le jour de la liberté complète.',
      },
      taxHouse: {
        title: 'MAISON DES IMPÔTS',
        description: 'C\'est la maison des impôts, où les taxes sont collectées dans les villages voisins.',
        taxSize: 'Taille de l\'Impôt:',
        liberationRange: 'libération:',
        currentLiberation: 'Pourcentage de libération actuel:',
        currentTax: 'Taille de l\'impôt actuel:',
        collectTaxes: 'Collecter les Impôts',
        collectTaxesCooldown: 'Collecter les Impôts',
        collectTaxesAmount: 'Collecter les Impôts',
      },
      orcAttack: {
        title: 'ATTAQUE ORC',
        description: 'Chaque heure, les orcs attaquent la base. Le nombre et la composition dépendent du pourcentage de libération.',
        currentAttack: 'Attaque Actuelle:',
        liberationPercent: 'Pourcentage de Libération:',
        orcCount: 'Nombre d\'Orcs:',
        composition: 'Composition:',
        attackIn: 'Attaque dans',
        attackReady: 'ATTAQUE PRÊTE !',
        attackTable: 'Tableau des Attaques:',
        range1: '0-10% libération',
        range2: '10-20% libération',
        range3: '20-30% libération',
        range4: '30-40% libération',
        range5: '40-50% libération',
        range6: '50-60% libération',
        range7: '60-70% libération',
        range8: '70-80% libération',
        range9: '80-90% libération',
        range10: '90-100% libération',
        startDefense: 'COMMENCER LA DÉFENSE',
        noFighters: 'Aucun combattant disponible pour la défense !',
        defense: 'Défense:',
        maxFighters: 'Maximum 15 combattants peuvent sortir en défense',
        mapSize: 'Taille de la carte: 15x15 cellules',
        attackFrequency: 'L\'attaque se produit toutes les heures',
        warning: '⚠️ Attention:',
        lossPenalty: 'Si vous perdez l\'attaque de la base, 20% des points de libération sont perdus',
        youngOrc: 'orcs jeunes',
        fighterOrc: 'combattants',
        veteranOrc: 'vétérans',
        cells: 'cellules',
        hours: 'heures',
        minutes: 'minutes',
        range1Desc: '3 orcs jeunes',
        range2Desc: '4 orcs (3 jeunes + 1 combattant)',
        range3Desc: '5 orcs (2 jeunes + 2 combattants + 1 vétéran)',
        range4Desc: '6 orcs (2 jeunes + 2 combattants + 2 vétérans)',
        range5Desc: '7 orcs (1 jeune + 3 combattants + 3 vétérans)',
        range6Desc: '8 orcs (1 jeune + 3 combattants + 4 vétérans)',
        range7Desc: '9 orcs (4 combattants + 5 vétérans)',
        range8Desc: '10 orcs (3 combattants + 7 vétérans)',
        range9Desc: '10 orcs (2 combattants + 8 vétérans)',
        range10Desc: '10 orcs (1 combattant + 9 vétérans)',
      },
      barracks: {
        title: 'CASERNE',
        notBuilt: 'Caserne non construite',
        build: 'Construire',
        buildCost: '(50 Métal)',
        recruit: 'Recruter',
        recruitCost: '(20 Or)',
        equipment: 'ÉQUIPEMENT:',
        newRecruit: 'ÉQUIPEMENT DE RECRUE',
        leftHand: 'GAUCHE',
        rightHand: 'DROITE',
        armor: 'ARMURE',
        remove: 'RETIRER',
        // ready: 'PRÊT POUR LA BATAILLE',
        statsUpdate: 'Les stats seront mises à jour après le recrutement',
        save: 'Enregistrer les modifications',
        cancel: 'ANNULER',
        weaponStats: 'Stats d\'arme',
        healthStamina: 'Santé et endurance',
        weaponStorage: 'Stockage d\'armes',
        ranks: 'Rangs de combattants',
        finishRecruitment: 'Terminer le recrutement',
        squad: 'Votre escouade',
        count: 'Nombre:',
        empty: 'La caserne est vide...',
        rank: 'Rang',
        progress: 'Progrès:',
        ready: 'PRÊT',
        dead: 'MORT',
        health: 'Santé',
        stamina: 'Endurance',
        rankRequired: 'RANG REQUIS',
        attack: 'ATQ:',
        actionPoints: 'PA:',
        range: 'PORT:',
        defense: 'DEF:',
        weight: 'POIDS:',
        twoHanded: '2M',
      },
      raid: {
        title: 'RAID',
        selectDepth: 'Sélectionner la profondeur du raid:',
        depth: 'PROFONDEUR',
        help: 'Aide',
        enemies: 'Ennemis',
        reward: 'Récompense:',
        selectFighters: 'Sélectionner les combattants',
        startRaid: 'AU COMBAT',
        selected: 'Sélectionné:',
        maxFighters: 'Max. 5 combattants',
        health: 'Santé',
        helpTitle: 'AIDE: PROFONDEUR {depth}',
        helpDescription: 'Ennemis possibles à cette profondeur:',
        stamina: 'Endurance:',
        attack: 'Attaque:',
        attackBlocked: 'Repoussez d\'abord l\'attaque orque',
        defense: 'Défense:',
        passive: 'passive',
        progress: 'Progrès:',
        weapons: 'Armes:',
        shields: 'Boucliers:',
        armor: 'Armure:',
      },
      temple: {
        title: 'TEMPLE',
        notBuilt: 'Temple non construit',
        build: 'Construire',
        buildCost: '(100 Bois)',
        healAll: 'TOUT SOIGNER',
        healDescription: '💚 Soin complet gratuit pour toutes les unités une fois par jour après un raid',
      },
      mageTower: {
        title: 'TOUR DU MAGE',
        notBuilt: 'Tour du Mage non construite',
        build: 'Construire',
        buildCost: '(30 Cristaux)',
        potions: '🧙‍♂️ POTIONS:',
        greenPotion: 'Potion Verte',
        greenPotionDesc: 'Soin lent entre les batailles',
        greenPotionEffect: 'Restaure complètement la santé de l\'unité sélectionnée avant la prochaine vague de raid',
        redPotion: 'Potion Rouge',
        redPotionDesc: 'Soin rapide en bataille',
        redPotionEffect: 'Restaure instantanément la santé complète de l\'unité pendant son tour',
        stock: 'Stock:',
        buy: 'ACHETER',
      },
    },
    battle: {
      victory: 'Victoire !',
      defeat: 'Défaite',
      retreat: 'Retraite',
      continue: 'Continuer',
      exit: 'Quitter',
      baseDefended: 'Base Défendue !',
      raidCompleted: 'Raid Terminé !',
      waveCompleted: 'Vague {wave} Terminée !',
      defenseReward: 'Récompense de Défense :',
      waveTrophies: 'Trophées de Vague {wave} :',
      gold: 'Or',
      metal: 'Métal',
      wood: 'Bois',
      leather: 'Cuir',
      crystals: 'Cristaux',
      liberation: 'Libération',
      squadStatus: 'État de l\'Escouade :',
      killed: 'TUÉ',
      continueBattle: 'AU COMBAT (VAGUE {wave})',
      takeAndRetreat: 'PRENDRE ET RETRAITER',
      returnToBase: 'RETOURNER À LA BASE',
      you: 'VOUS:',
      them: 'EUX:',
      queue: 'File',
      log: 'Journal',
    },
    fighterMenu: {
      title: 'Sélection des Combattants',
      yourFighters: 'Vos Combattants',
      addFighter: '+ Ajouter un Combattant',
      addTestFighters: '⚡ Ajouter Test (5)',
      leftHand: 'Main Gauche :',
      rightHand: 'Main Droite :',
      empty: 'Vide',
      attack: 'Attaque :',
      defense: 'Défense :',
      range: 'Portée :',
      actionPoints: 'Points d\'Action :',
      details: 'Détails du Combattant',
      health: 'Santé :',
      mapSize: 'Taille de la Carte :',
      startGame: 'Commencer le Jeu',
      replaceConfirm: 'Cela remplacera tous les combattants actuels. Continuer ?',
      incompatibleWeapon: 'Cette arme est incompatible avec l\'arme dans l\'autre main !',
      addAtLeastOne: 'Ajoutez au moins un combattant !',
      fighter: 'Combattant',
    },
    weapons: {
      club: 'Club',
      small_sword: 'Small Sword',
      large_sword: 'Large Sword',
      small_shield: 'Small Shield',
      large_shield: 'Large Shield',
      small_bow: 'Small Bow',
      large_bow: 'Large Bow',
      wooden_shield: 'Wooden Shield',
      leather_armor: 'Leather Armor',
      metal_armor: 'Metal Armor',
      bone_dagger: 'Bone Dagger',
      rusty_axe: 'Rusty Axe',
      stone_mace: 'Stone Mace',
      bone_shield_orc: 'Bone Orc Shield',
      wooden_shield_orc: 'Wooden Orc Shield',
      stone_shield: 'Stone Shield',
      throwing_tomahawk: 'Throwing Tomahawk',
      orc_spear: 'Orc Spear',
      mutant_leather: 'Mutant Leather',
      bone_armor: 'Bone Armor',
    },
    fighters: {
      recruit: 'Recruit',
      fighter: 'Fighter',
      veteran: 'Veteran',
    },
    enemies: {
      bone_cutter: 'Coupeur d\'Os',
      bone_killer: 'Tueur d\'Os',
      axe_shield: 'Hache-Bouclier',
      mutant_dagger: 'Mutant Dague',
      axe_wielder: 'Porteur de Hache',
      bone_axe_wielder: 'Porteur de Hache d\'Os',
      thrower: 'Lanceur',
      stone_destroyer: 'Destructeur de Pierre',
      spearman: 'Lancier',
    },
    buildings: {},
    tutorial: {
      next: 'Далее',
      understood: 'Понял, укрепляю базу!',
      skip: 'Пропустить',
      skipTutorial: 'Passer le Tutoriel',
      intro: {
        step1: 'Капитан... Вы очнулись. Порча... она пожрала всё.',
        step2: 'Три дня назад культисты открыли Врата Бездны.\nЧёрная слизь мутировала наших братьев — людей в орков,\nгномов в троллей, эльфов в пауков.',
        step3: 'Теперь они ломятся в наш последний оплот на равнинах.\nМы — всё, что осталось от королевства Эридан.',
        step4: 'Ты — Капитан Выживших. Твоя задача:\n• Строить базу — казармы, кузницу, храм\n• Нанимать и вооружать бойцов\n• Отбивать ночные нападения\n• Проводить рейды, отвоёвывая землю',
        step5: 'Каждый день Порча усиливается.\nЗаполни Очки Освобождения — разблокируй союзные биомы:\nлеса эльфов, горы гномов, болота дварфов.',
        step6: 'Если падёт наш оплот — конец. Закрой Врата Бездны... или умри вместе с нами.',
      },
      base: {
        barracks: { title: 'Казарма', description: 'Казарма нужна для набора новобранцев, так же здесь располагаются все наши войска.' },
        raid: { title: 'Ворота', description: 'Ворота, через них отправляются отряды наших доблестных воинов вглубь территории для освобождения наших земель от порождений скверны.' },
        forge: { title: 'Кузница', description: 'Кузница, здесь создаётся оружие и броня для наших воинов.' },
        temple: { title: 'Храм', description: 'Храм, здесь с божьего благословения наши воины могут излечить свои раны.' },
        mageTower: { title: 'Башня мага', description: 'Башня мага, здесь маги варят зелья жизни и выносливости для поддержания наших воинов.' },
        castle: { title: 'Замок', description: 'Замок, здесь пишется летопись этой эпохи.' },
        taxHouse: { title: 'Дом старосты', description: 'Дом старосты, здесь собираются налоги с окрестных деревень. Налоги можно собирать раз в 15 минут, размер налога зависит от процента освобождения территории.' },
        orcAttack: { title: 'Нападение орков', description: 'Каждый час на базу нападают орки. Количество и состав врагов зависят от процента освобождения - чем выше освобождение, тем сильнее и больше врагов. При проигрыше в нападении теряется 20% очков освобождения.' },
        all: { title: 'В путь!', description: 'Найми первых новобранцев и отправляйся в свой первый рейд, чтобы освободить королевство от захватчиков.' },
      },
      extended: {
        resources: {
          liberation: { title: 'Очки освобождения', description: 'Это процент освобождённой территории. Когда достигнете 100% - завершится демо-версия. В полной версии вас ждут цитадели Эльфов, Гномов, Номадов и Ящеролюдей!' },
          gold: { title: 'Золото', description: 'Основная валюта. Нужно для найма бойцов, постройки зданий и покупки оружия.' },
          metal: { title: 'Металл', description: 'Нужен для создания оружия и брони в кузнице.' },
          wood: { title: 'Дерево', description: 'Нужно для создания щитов и некоторых видов оружия в кузнице.' },
          leather: { title: 'Кожа', description: 'Нужна для создания доспехов в кузнице.' },
          crystals: { title: 'Кристаллы', description: 'Редкий ресурс. Нужны для покупки зелий в башне мага.' },
          fighters: { title: 'Бойцы', description: 'Количество ваших воинов. Формат: текущее/максимум. Максимум можно увеличить через постройки.' },
          potions: { title: 'Зелья', description: 'Зелёное зелье - медленное лечение между боями. Красное зелье - быстрое лечение в бою.' },
        },
        taxHouse: {
          icon: { title: 'Дом старосты', description: 'Нажмите на иконку дома, чтобы открыть окно сбора налогов.' },
          collect: { title: 'Собрать налоги', description: 'Нажмите кнопку "Собрать налоги", чтобы получить золото. Налоги можно собирать раз в 15 минут.' },
        },
        barracks: {
          icon: { title: 'Казарма', description: 'Нажмите на иконку казармы, чтобы открыть окно управления войсками.' },
          build: { title: 'Построить казарму', description: 'Сначала нужно построить казарму. Нажмите кнопку "Построить".' },
          recruit: { title: 'Нанять бойца', description: 'Теперь найдите бойца и нажмите кнопку "Нанять".' },
          equip: { title: 'Экипировать бойца', description: 'Выберите оружие и щит для бойца, затем нажмите "Экипировать".' },
        },
        raid: {
          icon: { title: 'Ворота', description: 'Нажмите на иконку ворот, чтобы открыть окно выбора рейда.' },
          select: { title: 'Выбрать рейд', description: 'Выберите глубину рейда и бойцов для участия.' },
          start: { title: 'Начать рейд', description: 'Нажмите кнопку "Начать рейд", чтобы отправиться в бой.' },
        },
        battle: {
          unit: { title: 'Информация о юните', description: 'Здесь отображается имя, стамина и здоровье текущего юнита.' },
          movement: { title: 'Движение', description: 'Кликните на клетку, чтобы переместить юнита. Синие клетки - доступны для движения.' },
          obstacles: { title: 'Препятствия', description: 'Дерево - нельзя пройти и стрелять. Каряга - нельзя пройти, но можно стрелять. Куст - можно пройти, но тратится 2 стамины.' },
          attack: { title: 'Атака', description: 'Кликните на врага, чтобы атаковать. Красные клетки - враги в зоне атаки.' },
          defense: { title: 'Защита', description: 'Нажмите кнопку "ЩИТ", чтобы активировать защиту. Это уменьшит получаемый урон.' },
          turn: { title: 'Ход', description: 'Нажмите кнопку "ХОД", чтобы завершить ход текущего юнита.' },
          queue: { title: 'Очередь', description: 'Здесь отображается очередь ходов всех юнитов.' },
          end: { title: 'Завершение', description: 'Поздравляем! Вы завершили обучение. Теперь вы готовы к бою!' },
        },
      },
    },
  },
  de: {
    common: {
      play: 'SPIELEN',
      newGame: 'NEUES SPIEL',
      settings: 'EINSTELLUNGEN',
      back: 'Zurück',
      next: 'Weiter',
      close: 'Schließen',
      confirm: 'Bestätigen',
      cancel: 'Abbrechen',
      yes: 'Ja',
      no: 'Nein',
      backToMenu: 'Zum Hauptmenü',
      name: 'Name',
      stamina: 'Ausdauer',
    },
    menu: {
      play: 'SPIELEN',
      newGame: 'NEUES SPIEL',
      settings: 'EINSTELLUNGEN',
      newGameConfirm: 'Sind Sie sicher, dass Sie von vorne beginnen möchten? Alle Fortschritte gehen verloren.',
    },
    settings: {
      title: 'Einstellungen',
      language: 'Sprache',
      selectLanguage: 'Sprache auswählen',
      music: 'Musik',
      musicEnabled: 'Aktiviert',
      musicDisabled: 'Deaktiviert',
      volume: 'Lautstärke',
      enableMusic: 'Musik Aktivieren',
      disableMusic: 'Musik Deaktivieren',
    },
    game: {
      turn: 'ZUG',
      enemyTurn: 'Gegnerzug...',
      yourUnits: 'SIE',
      enemyUnits: 'SIE',
      grid: 'Raster',
      gridOn: 'AN',
      gridOff: 'AUS',
      queue: 'Warteschlange',
      log: 'Protokoll',
      center: '🎯',
      stamina: 'Ausdauer',
      health: 'LP',
      power: 'Kraft',
      defense: 'Verteidigung',
      endTurn: 'ZUG',
      shield: 'SCHILD',
      removeShield: 'ENTFERNEN',
      retreat: 'RÜCKZUG',
      undo: 'RÜCKGÄNGIG',
      redPotion: '❤️',
      redPotionTooltip: 'Roter Trank - Vollständige Heilung',
      clickBaseHint: 'KLICKEN SIE AUF DIE ZITADELLE, UM EINZUTRETEN',
      loadingWorld: 'WELT WIRD GELADEN...',

      zoomIn: 'Vergrößern',
      zoomOut: 'Verkleinern',
      centerOnFighter: 'Zentrieren',
    },
    base: {
      title: 'Basis',
      citadel: 'MENSCHLICHE ZITADELLE',
      resources: 'Ressourcen',
      gold: 'Gold',
      metal: 'Metall',
      wood: 'Holz',
      leather: 'Leder',
      crystals: 'Kristalle',
      liberation: 'Befreiung',
      buildings: 'Gebäude',
      fighters: 'Kämpfer',
      barracks: 'Kaserne',
      forge: 'Schmiede',
      temple: 'Tempel',
      mageTower: 'Magierturm',
      raid: 'Überfall',
      castle: 'Schloss',
      taxHouse: 'Haus',
      orcAttack: 'Ork-Angriff',
      attackAlert: 'ANGRIFF!',
      greenPotion: 'Grüner Trank',
      redPotion: 'Roter Trank',
    },
    modals: {
      forge: {
        title: 'SCHMIEDE',
        notBuilt: 'Schmiede nicht gebaut',
        build: 'Bauen',
        buildCost: '(50 Metall)',
        categories: {
          melee: 'Waffen',
          shields: 'Schilde',
          armor: 'Rüstung',
          ranged: 'Bögen',
        },
        buy: 'KAUFEN',
        cost: 'Kosten:',
        rank: 'Rang',
        inventory: 'Im Inventar:',
        attack: 'ANG:',
        defenseActive: 'VER (A):',
        defensePassive: 'VER (P):',
        actionPoints: 'AP:',
        range: 'REICHW:',
        weight: 'GEWICHT:',
        stock: 'Bestand:',
        emptyCategory: 'Keine Gegenstände in dieser Kategorie',
        hoverHint: 'Bewegen Sie die Maus über einen Gegenstand, um Statistiken anzuzeigen',
      },
      castle: {
        title: 'HAUPTSCHLOSS',
        treasury: 'Königliche Kanzlei',
        loyalty: 'Loyalität:',
        army: 'Armee:',
        conquestDiary: 'Eroberungstagebuch:',
        depth: 'TIEFE',
        description: 'Hier wird die Geschichte Ihres Königreichs geschmiedet. Jeder Sieg bringt den Tag der vollständigen Freiheit näher.',
      },
      taxHouse: {
        title: 'STEUERHAUS',
        description: 'Dies ist das Steuerhaus, wo Steuern aus den nahegelegenen Dörfern gesammelt werden.',
        taxSize: 'Steuergröße:',
        liberationRange: 'Befreiung:',
        currentLiberation: 'Aktueller Befreiungsprozentsatz:',
        currentTax: 'Aktuelle Steuergröße:',
        collectTaxes: 'Steuern Sammeln',
        collectTaxesCooldown: 'Steuern Sammeln',
        collectTaxesAmount: 'Steuern Sammeln',
      },
      orcAttack: {
        title: 'ORK-ANGRIFF',
        description: 'Jede Stunde greifen Orks die Basis an. Die Anzahl und Zusammensetzung hängen vom Befreiungsprozentsatz ab.',
        currentAttack: 'Aktueller Angriff:',
        liberationPercent: 'Befreiungsprozentsatz:',
        orcCount: 'Anzahl der Orks:',
        composition: 'Zusammensetzung:',
        attackIn: 'Angriff in',
        attackReady: 'ANGRIFF BEREIT!',
        attackTable: 'Angriffstabelle:',
        range1: '0-10% Befreiung',
        range2: '10-20% Befreiung',
        range3: '20-30% Befreiung',
        range4: '30-40% Befreiung',
        range5: '40-50% Befreiung',
        range6: '50-60% Befreiung',
        range7: '60-70% Befreiung',
        range8: '70-80% Befreiung',
        range9: '80-90% Befreiung',
        range10: '90-100% Befreiung',
        startDefense: 'VERTEIDIGUNG STARTEN',
        noFighters: 'Keine verfügbaren Kämpfer für die Verteidigung!',
        defense: 'Verteidigung:',
        maxFighters: 'Maximal 15 Kämpfer können zur Verteidigung ausgehen',
        mapSize: 'Kartengröße: 15x15 Zellen',
        attackFrequency: 'Angriff erfolgt jede Stunde',
        warning: '⚠️ Warnung:',
        lossPenalty: 'Wenn Sie den Basisangriff verlieren, gehen 20% der Befreiungspunkte verloren',
        youngOrc: 'junge Orks',
        fighterOrc: 'Kämpfer',
        veteranOrc: 'Veteranen',
        cells: 'Zellen',
        hours: 'Stunden',
        minutes: 'Minuten',
        range1Desc: '3 junge Orks',
        range2Desc: '4 Orks (3 junge + 1 Kämpfer)',
        range3Desc: '5 Orks (2 junge + 2 Kämpfer + 1 Veteran)',
        range4Desc: '6 Orks (2 junge + 2 Kämpfer + 2 Veteranen)',
        range5Desc: '7 Orks (1 junger + 3 Kämpfer + 3 Veteranen)',
        range6Desc: '8 Orks (1 junger + 3 Kämpfer + 4 Veteranen)',
        range7Desc: '9 Orks (4 Kämpfer + 5 Veteranen)',
        range8Desc: '10 Orks (3 Kämpfer + 7 Veteranen)',
        range9Desc: '10 Orks (2 Kämpfer + 8 Veteranen)',
        range10Desc: '10 Orks (1 Kämpfer + 9 Veteranen)',
      },
      barracks: {
        title: 'KASERNE',
        notBuilt: 'Kaserne nicht gebaut',
        build: 'Bauen',
        buildCost: '(50 Metall)',
        recruit: 'Rekrutieren',
        recruitCost: '(20 Gold)',
        equipment: 'AUSRÜSTUNG:',
        newRecruit: 'REKRUTENAUSRÜSTUNG',
        leftHand: 'LINKS',
        rightHand: 'RECHTS',
        armor: 'RÜSTUNG',
        remove: 'ENTFERNEN',
        // ready: 'KAMPFBEREIT',
        statsUpdate: 'Stats werden nach Rekrutierung aktualisiert',
        save: 'Änderungen speichern',
        cancel: 'ABBRECHEN',
        weaponStats: 'Waffenstats',
        healthStamina: 'Gesundheit und Ausdauer',
        weaponStorage: 'Waffenlager',
        ranks: 'Kämpferränge',
        finishRecruitment: 'Rekrutierung abschließen',
        squad: 'Ihr Trupp',
        count: 'Anzahl:',
        empty: 'Kaserne ist leer...',
        rank: 'Rang',
        progress: 'Fortschritt:',
        ready: 'BEREIT',
        dead: 'TOT',
        health: 'Gesundheit',
        stamina: 'Ausdauer',
        rankRequired: 'RANG ERFORDERLICH',
        attack: 'ANG:',
        actionPoints: 'AP:',
        range: 'REICHW:',
        defense: 'VER:',
        weight: 'GEWICHT:',
        twoHanded: '2H',
      },
      raid: {
        title: 'ÜBERFALL',
        selectDepth: 'Überfalltiefe auswählen:',
        depth: 'TIEFE',
        help: 'Hilfe',
        enemies: 'Feinde',
        reward: 'Belohnung:',
        selectFighters: 'Kämpfer auswählen',
        startRaid: 'ZUM KAMPF',
        selected: 'Ausgewählt:',
        maxFighters: 'Max. 5 Kämpfer',
        health: 'Gesundheit',
        helpTitle: 'HILFE: TIEFE {depth}',
        helpDescription: 'Mögliche Feinde in dieser Tiefe:',
        stamina: 'Ausdauer:',
        attack: 'Angriff:',
        defense: 'Verteidigung:',
        passive: 'passiv',
        progress: 'Fortschritt:',
        weapons: 'Waffen:',
        shields: 'Schilde:',
        armor: 'Rüstung:',
        attackBlocked: 'Zuerst die Ork-Attacke abwehren',
      },
      temple: {
        title: 'TEMPEL',
        notBuilt: 'Tempel nicht gebaut',
        build: 'Bauen',
        buildCost: '(100 Holz)',
        healAll: 'ALLE HEILEN',
        healDescription: '💚 Kostenlose vollständige Heilung für alle Einheiten einmal täglich nach einem Überfall',
      },
      mageTower: {
        title: 'MAGIERTURM',
        notBuilt: 'Magierturm nicht gebaut',
        build: 'Bauen',
        buildCost: '(30 Kristalle)',
        potions: '🧙‍♂️ TRÄNKE:',
        greenPotion: 'Grüner Trank',
        greenPotionDesc: 'Langsame Heilung zwischen den Kämpfen',
        greenPotionEffect: 'Stellt die Gesundheit der ausgewählten Einheit vor der nächsten Überfallwelle vollständig wieder her',
        redPotion: 'Roter Trank',
        redPotionDesc: 'Schnelle Heilung im Kampf',
        redPotionEffect: 'Stellt die Gesundheit der Einheit während ihres Zuges sofort vollständig wieder her',
        stock: 'Bestand:',
        buy: 'KAUFEN',
      },
    },
    battle: {
      victory: 'Sieg!',
      defeat: 'Niederlage',
      retreat: 'Rückzug',
      continue: 'Weiter',
      exit: 'Beenden',
      baseDefended: 'Basis Verteidigt!',
      raidCompleted: 'Überfall Abgeschlossen!',
      waveCompleted: 'Welle {wave} Abgeschlossen!',
      defenseReward: 'Verteidigungsbelohnung:',
      waveTrophies: 'Welle {wave} Trophäen:',
      gold: 'Gold',
      metal: 'Metall',
      wood: 'Holz',
      leather: 'Leder',
      crystals: 'Kristalle',
      liberation: 'Befreiung',
      squadStatus: 'Truppstatus:',
      killed: 'GETÖTET',
      continueBattle: 'ZUM KAMPF (WELLE {wave})',
      takeAndRetreat: 'NEHMEN UND ZURÜCKZIEHEN',
      returnToBase: 'ZURÜCK ZUR BASIS',
      you: 'DU:',
      them: 'SIE:',
      queue: 'Warteschlange',
      log: 'Protokoll',
    },
    fighterMenu: {
      title: 'Kämpferauswahl',
      yourFighters: 'Ihre Kämpfer',
      addFighter: '+ Kämpfer hinzufügen',
      addTestFighters: '⚡ Test hinzufügen (5)',
      leftHand: 'Linke Hand:',
      rightHand: 'Rechte Hand:',
      empty: 'Leer',
      attack: 'Angriff:',
      defense: 'Verteidigung:',
      range: 'Reichweite:',
      actionPoints: 'Aktionspunkte:',
      details: 'Kämpferdetails',
      health: 'Gesundheit:',
      mapSize: 'Kartengröße:',
      startGame: 'Spiel starten',
      replaceConfirm: 'Dies ersetzt alle aktuellen Kämpfer. Fortfahren?',
      incompatibleWeapon: 'Diese Waffe ist nicht mit der Waffe in der anderen Hand kompatibel!',
      addAtLeastOne: 'Fügen Sie mindestens einen Kämpfer hinzu!',
      fighter: 'Kämpfer',
    },
    weapons: {
      club: 'Club',
      small_sword: 'Small Sword',
      large_sword: 'Large Sword',
      small_shield: 'Small Shield',
      large_shield: 'Large Shield',
      small_bow: 'Small Bow',
      large_bow: 'Large Bow',
      wooden_shield: 'Wooden Shield',
      leather_armor: 'Leather Armor',
      metal_armor: 'Metal Armor',
      bone_dagger: 'Bone Dagger',
      rusty_axe: 'Rusty Axe',
      stone_mace: 'Stone Mace',
      bone_shield_orc: 'Bone Orc Shield',
      wooden_shield_orc: 'Wooden Orc Shield',
      stone_shield: 'Stone Shield',
      throwing_tomahawk: 'Throwing Tomahawk',
      orc_spear: 'Orc Spear',
      mutant_leather: 'Mutant Leather',
      bone_armor: 'Bone Armor',
    },
    fighters: {
      recruit: 'Recruit',
      fighter: 'Fighter',
      veteran: 'Veteran',
    },
    enemies: {
      bone_cutter: 'Knochenschneider',
      bone_killer: 'Knochentöter',
      axe_shield: 'Axt-Schild',
      mutant_dagger: 'Mutant Dolch',
      axe_wielder: 'Axtträger',
      bone_axe_wielder: 'Knochenaxtträger',
      thrower: 'Werfer',
      stone_destroyer: 'Steinzerstörer',
      spearman: 'Speerträger',
    },
    buildings: {},
    tutorial: {
      next: 'Далее',
      understood: 'Понял, укрепляю базу!',
      skip: 'Пропустить',
      skipTutorial: 'Tutorial Überspringen',
      intro: {
        step1: 'Капитан... Вы очнулись. Порча... она пожрала всё.',
        step2: 'Три дня назад культисты открыли Врата Бездны.\nЧёрная слизь мутировала наших братьев — людей в орков,\nгномов в троллей, эльфов в пауков.',
        step3: 'Теперь они ломятся в наш последний оплот на равнинах.\nМы — всё, что осталось от королевства Эридан.',
        step4: 'Ты — Капитан Выживших. Твоя задача:\n• Строить базу — казармы, кузницу, храм\n• Нанимать и вооружать бойцов\n• Отбивать ночные нападения\n• Проводить рейды, отвоёвывая землю',
        step5: 'Каждый день Порча усиливается.\nЗаполни Очки Освобождения — разблокируй союзные биомы:\nлеса эльфов, горы гномов, болота дварфов.',
        step6: 'Если падёт наш оплот — конец. Закрой Врата Бездны... или умри вместе с нами.',
      },
      base: {
        barracks: { title: 'Казарма', description: 'Казарма нужна для набора новобранцев, так же здесь располагаются все наши войска.' },
        raid: { title: 'Ворота', description: 'Ворота, через них отправляются отряды наших доблестных воинов вглубь территории для освобождения наших земель от порождений скверны.' },
        forge: { title: 'Кузница', description: 'Кузница, здесь создаётся оружие и броня для наших воинов.' },
        temple: { title: 'Храм', description: 'Храм, здесь с божьего благословения наши воины могут излечить свои раны.' },
        mageTower: { title: 'Башня мага', description: 'Башня мага, здесь маги варят зелья жизни и выносливости для поддержания наших воинов.' },
        castle: { title: 'Замок', description: 'Замок, здесь пишется летопись этой эпохи.' },
        taxHouse: { title: 'Дом старосты', description: 'Дом старосты, здесь собираются налоги с окрестных деревень. Налоги можно собирать раз в 15 минут, размер налога зависит от процента освобождения территории.' },
        orcAttack: { title: 'Нападение орков', description: 'Каждый час на базу нападают орки. Количество и состав врагов зависят от процента освобождения - чем выше освобождение, тем сильнее и больше врагов. При проигрыше в нападении теряется 20% очков освобождения.' },
        all: { title: 'В путь!', description: 'Найми первых новобранцев и отправляйся в свой первый рейд, чтобы освободить королевство от захватчиков.' },
      },
      extended: {
        resources: {
          liberation: { title: 'Очки освобождения', description: 'Это процент освобождённой территории. Когда достигнете 100% - завершится демо-версия. В полной версии вас ждут цитадели Эльфов, Гномов, Номадов и Ящеролюдей!' },
          gold: { title: 'Золото', description: 'Основная валюта. Нужно для найма бойцов, постройки зданий и покупки оружия.' },
          metal: { title: 'Металл', description: 'Нужен для создания оружия и брони в кузнице.' },
          wood: { title: 'Дерево', description: 'Нужно для создания щитов и некоторых видов оружия в кузнице.' },
          leather: { title: 'Кожа', description: 'Нужна для создания доспехов в кузнице.' },
          crystals: { title: 'Кристаллы', description: 'Редкий ресурс. Нужны для покупки зелий в башне мага.' },
          fighters: { title: 'Бойцы', description: 'Количество ваших воинов. Формат: текущее/максимум. Максимум можно увеличить через постройки.' },
          potions: { title: 'Зелья', description: 'Зелёное зелье - медленное лечение между боями. Красное зелье - быстрое лечение в бою.' },
        },
        taxHouse: {
          icon: { title: 'Дом старосты', description: 'Нажмите на иконку дома, чтобы открыть окно сбора налогов.' },
          collect: { title: 'Собрать налоги', description: 'Нажмите кнопку "Собрать налоги", чтобы получить золото. Налоги можно собирать раз в 15 минут.' },
        },
        barracks: {
          icon: { title: 'Казарма', description: 'Нажмите на иконку казармы, чтобы открыть окно управления войсками.' },
          build: { title: 'Построить казарму', description: 'Сначала нужно построить казарму. Нажмите кнопку "Построить".' },
          recruit: { title: 'Нанять бойца', description: 'Теперь найдите бойца и нажмите кнопку "Нанять".' },
          equip: { title: 'Экипировать бойца', description: 'Выберите оружие и щит для бойца, затем нажмите "Экипировать".' },
        },
        raid: {
          icon: { title: 'Ворота', description: 'Нажмите на иконку ворот, чтобы открыть окно выбора рейда.' },
          select: { title: 'Выбрать рейд', description: 'Выберите глубину рейда и бойцов для участия.' },
          start: { title: 'Начать рейд', description: 'Нажмите кнопку "Начать рейд", чтобы отправиться в бой.' },
        },
        battle: {
          unit: { title: 'Информация о юните', description: 'Здесь отображается имя, стамина и здоровье текущего юнита.' },
          movement: { title: 'Движение', description: 'Кликните на клетку, чтобы переместить юнита. Синие клетки - доступны для движения.' },
          obstacles: { title: 'Препятствия', description: 'Дерево - нельзя пройти и стрелять. Каряга - нельзя пройти, но можно стрелять. Куст - можно пройти, но тратится 2 стамины.' },
          attack: { title: 'Атака', description: 'Кликните на врага, чтобы атаковать. Красные клетки - враги в зоне атаки.' },
          defense: { title: 'Защита', description: 'Нажмите кнопку "ЩИТ", чтобы активировать защиту. Это уменьшит получаемый урон.' },
          turn: { title: 'Ход', description: 'Нажмите кнопку "ХОД", чтобы завершить ход текущего юнита.' },
          queue: { title: 'Очередь', description: 'Здесь отображается очередь ходов всех юнитов.' },
          end: { title: 'Завершение', description: 'Поздравляем! Вы завершили обучение. Теперь вы готовы к бою!' },
        },
      },
    },
  },
  zh: {
    common: {
      play: '游戏',
      newGame: '新游戏',
      settings: '设置',
      back: '返回',
      next: '下一步',
      close: '关闭',
      confirm: '确认',
      cancel: '取消',
      yes: '是',
      no: '否',
      backToMenu: '返回主菜单',
      name: '名称',
      stamina: '体力',
    },
    menu: {
      play: '游戏',
      newGame: '新游戏',
      settings: '设置',
      newGameConfirm: '您确定要重新开始吗？所有进度都将丢失。',
    },
    settings: {
      title: '设置',
      language: '语言',
      selectLanguage: '选择语言',
      music: '音乐',
      musicEnabled: '已启用',
      musicDisabled: '已禁用',
      volume: '音量',
      enableMusic: '启用音乐',
      disableMusic: '禁用音乐',
    },
    game: {
      turn: '回合',
      enemyTurn: '敌人回合...',
      yourUnits: '你',
      enemyUnits: '他们',
      grid: '网格',
      gridOn: '开',
      gridOff: '关',
      queue: '队列',
      log: '日志',
      center: '🎯',
      stamina: '体力',
      health: '生命值',
      power: '力量',
      defense: '防御',
      endTurn: '回合',
      shield: '盾牌',
      removeShield: '移除',
      retreat: '撤退',
      undo: '撤销',
      redPotion: '❤️',
      redPotionTooltip: '红药水 - 完全治疗',
      clickBaseHint: '点击要塞进入',
      loadingWorld: '加载世界中...',

      zoomIn: '放大',
      zoomOut: '缩小',
      centerOnFighter: '居中',
    },
    base: {
      title: '基地',
      citadel: '人类要塞',
      resources: '资源',
      gold: '黄金',
      metal: '金属',
      wood: '木材',
      leather: '皮革',
      crystals: '水晶',
      liberation: '解放',
      buildings: '建筑',
      fighters: '战士',
      barracks: '兵营',
      forge: '锻造厂',
      temple: '神庙',
      mageTower: '法师塔',
      raid: '突袭',
      castle: '城堡',
      taxHouse: '房屋',
      orcAttack: '兽人攻击',
      attackAlert: '攻击！',
      greenPotion: '绿药水',
      redPotion: '红药水',
    },
    modals: {
      forge: {
        title: '锻造厂',
        notBuilt: '锻造厂未建造',
        build: '建造',
        buildCost: '(50 金属)',
        categories: {
          melee: '武器',
          shields: '盾牌',
          armor: '护甲',
          ranged: '弓箭',
        },
        buy: '购买',
        cost: '成本:',
        rank: '等级',
        inventory: '库存中:',
        attack: '攻击:',
        defenseActive: '防御(主动):',
        defensePassive: '防御(被动):',
        actionPoints: '行动点:',
        range: '射程:',
        weight: '重量:',
        stock: '库存:',
        emptyCategory: '此类别中没有物品',
        hoverHint: '将鼠标悬停在物品上以查看统计信息',
      },
      castle: {
        title: '主城堡',
        treasury: '皇家总理府',
        loyalty: '忠诚度:',
        army: '军队:',
        conquestDiary: '征服日记:',
        depth: '深度',
        description: '这里锻造着您王国的历史。每一次胜利都让完全自由的日子更近一步。',
      },
      taxHouse: {
        title: '税务所',
        description: '这是税务所，在这里从附近村庄收集税款。',
        taxSize: '税额:',
        liberationRange: '解放:',
        currentLiberation: '当前解放百分比:',
        currentTax: '当前税额:',
        collectTaxes: '收税',
        collectTaxesCooldown: '收税',
        collectTaxesAmount: '收税',
      },
      orcAttack: {
        title: '兽人攻击',
        description: '每小时兽人都会攻击基地。数量和组成取决于解放百分比。',
        currentAttack: '当前攻击:',
        liberationPercent: '解放百分比:',
        orcCount: '兽人数量:',
        composition: '组成:',
        attackIn: '攻击在',
        attackReady: '攻击就绪！',
        attackTable: '攻击表:',
        range1: '0-10% 解放',
        range2: '10-20% 解放',
        range3: '20-30% 解放',
        range4: '30-40% 解放',
        range5: '40-50% 解放',
        range6: '50-60% 解放',
        range7: '60-70% 解放',
        range8: '70-80% 解放',
        range9: '80-90% 解放',
        range10: '90-100% 解放',
        startDefense: '开始防御',
        noFighters: '没有可用的战士进行防御！',
        defense: '防御:',
        maxFighters: '最多15名战士可以参加防御',
        mapSize: '地图大小: 15x15 单元格',
        attackFrequency: '攻击每小时发生一次',
        warning: '⚠️ 警告:',
        lossPenalty: '如果您在基地攻击中失败，将失去20%的解放点数',
        youngOrc: '年轻兽人',
        fighterOrc: '战士',
        veteranOrc: '老兵',
        cells: '单元格',
        hours: '小时',
        minutes: '分钟',
        range1Desc: '3个年轻兽人',
        range2Desc: '4个兽人 (3个年轻 + 1个战士)',
        range3Desc: '5个兽人 (2个年轻 + 2个战士 + 1个老兵)',
        range4Desc: '6个兽人 (2个年轻 + 2个战士 + 2个老兵)',
        range5Desc: '7个兽人 (1个年轻 + 3个战士 + 3个老兵)',
        range6Desc: '8个兽人 (1个年轻 + 3个战士 + 4个老兵)',
        range7Desc: '9个兽人 (4个战士 + 5个老兵)',
        range8Desc: '10个兽人 (3个战士 + 7个老兵)',
        range9Desc: '10个兽人 (2个战士 + 8个老兵)',
        range10Desc: '10个兽人 (1个战士 + 9个老兵)',
      },
      barracks: {
        title: '兵营',
        notBuilt: '兵营未建造',
        build: '建造',
        buildCost: '(50 金属)',
        recruit: '招募',
        recruitCost: '(20 黄金)',
        equipment: '装备:',
        newRecruit: '新兵装备',
        leftHand: '左手',
        rightHand: '右手',
        armor: '护甲',
        remove: '移除',
        // ready: '准备战斗',
        statsUpdate: '招募后统计将更新',
        save: '保存更改',
        cancel: '取消',
        weaponStats: '武器统计',
        healthStamina: '生命值和体力',
        weaponStorage: '武器仓库',
        ranks: '战士等级',
        finishRecruitment: '完成招募',
        squad: '您的队伍',
        count: '数量:',
        empty: '兵营是空的...',
        rank: '等级',
        progress: '进度:',
        ready: '准备',
        dead: '死亡',
        health: '生命值',
        stamina: '体力',
        rankRequired: '需要等级',
        attack: '攻击:',
        actionPoints: '行动点:',
        range: '射程:',
        defense: '防御:',
        weight: '重量:',
        twoHanded: '双手',
      },
      raid: {
        title: '突袭',
        selectDepth: '选择突袭深度:',
        depth: '深度',
        help: '帮助',
        enemies: '敌人',
        reward: '奖励:',
        selectFighters: '选择战士',
        startRaid: '开始战斗',
        selected: '已选择:',
        maxFighters: '最多 5 名战士',
        health: '生命值',
        helpTitle: '帮助: 深度 {depth}',
        helpDescription: '此深度的可能敌人:',
        stamina: '体力:',
        attack: '攻击:',
        attackBlocked: '先击退兽人进攻',
        defense: '防御:',
        passive: '被动',
        progress: '进度:',
        weapons: '武器:',
        shields: '盾牌:',
        armor: '护甲:',
      },
      temple: {
        title: '神庙',
        notBuilt: '神庙未建造',
        build: '建造',
        buildCost: '(100 木材)',
        healAll: '治愈所有人',
        healDescription: '💚 突袭后每天一次所有单位免费完全治愈',
      },
      mageTower: {
        title: '法师塔',
        notBuilt: '法师塔未建造',
        build: '建造',
        buildCost: '(30 水晶)',
        potions: '🧙‍♂️ 药水:',
        greenPotion: '绿药水',
        greenPotionDesc: '战斗间隙缓慢治疗',
        greenPotionEffect: '在下一波突袭之前完全恢复所选单位的生命值',
        redPotion: '红药水',
        redPotionDesc: '战斗中快速治疗',
        redPotionEffect: '在单位行动期间立即完全恢复其生命值',
        stock: '库存:',
        buy: '购买',
      },
    },
    battle: {
      victory: '胜利！',
      defeat: '失败',
      retreat: '撤退',
      continue: '继续',
      exit: '退出',
      baseDefended: '基地已防御！',
      raidCompleted: '突袭完成！',
      waveCompleted: '第{wave}波完成！',
      defenseReward: '防御奖励：',
      waveTrophies: '第{wave}波战利品：',
      gold: '黄金',
      metal: '金属',
      wood: '木材',
      leather: '皮革',
      crystals: '水晶',
      liberation: '解放',
      squadStatus: '小队状态：',
      killed: '阵亡',
      continueBattle: '继续战斗（第{wave}波）',
      takeAndRetreat: '带走并撤退',
      returnToBase: '返回基地',
      you: '您:',
      them: '他们:',
      queue: '队列',
      log: '日志',
    },
    fighterMenu: {
      title: '选择战士',
      yourFighters: '您的战士',
      addFighter: '+ 添加战士',
      addTestFighters: '⚡ 添加测试 (5)',
      leftHand: '左手:',
      rightHand: '右手:',
      empty: '空',
      attack: '攻击:',
      defense: '防御:',
      range: '射程:',
      actionPoints: '行动点:',
      details: '战士详情',
      health: '生命值:',
      mapSize: '地图大小:',
      startGame: '开始游戏',
      replaceConfirm: '这将替换所有当前战士。继续？',
      incompatibleWeapon: '此武器与另一只手的武器不兼容！',
      addAtLeastOne: '请至少添加一名战士！',
      fighter: '战士',
    },
    weapons: {
      club: 'Club',
      small_sword: 'Small Sword',
      large_sword: 'Large Sword',
      small_shield: 'Small Shield',
      large_shield: 'Large Shield',
      small_bow: 'Small Bow',
      large_bow: 'Large Bow',
      wooden_shield: 'Wooden Shield',
      leather_armor: 'Leather Armor',
      metal_armor: 'Metal Armor',
      bone_dagger: 'Bone Dagger',
      rusty_axe: 'Rusty Axe',
      stone_mace: 'Stone Mace',
      bone_shield_orc: 'Bone Orc Shield',
      wooden_shield_orc: 'Wooden Orc Shield',
      stone_shield: 'Stone Shield',
      throwing_tomahawk: 'Throwing Tomahawk',
      orc_spear: 'Orc Spear',
      mutant_leather: 'Mutant Leather',
      bone_armor: 'Bone Armor',
    },
    fighters: {
      recruit: 'Recruit',
      fighter: 'Fighter',
      veteran: 'Veteran',
    },
    enemies: {
      bone_cutter: '切骨者',
      bone_killer: '骨杀手',
      axe_shield: '斧盾兵',
      mutant_dagger: '变异匕首',
      axe_wielder: '持斧者',
      bone_axe_wielder: '骨斧兵',
      thrower: '投掷者',
      stone_destroyer: '石破坏者',
      spearman: '长矛兵',
    },
    buildings: {},
    tutorial: {
      next: 'Далее',
      understood: 'Понял, укрепляю базу!',
      skip: 'Пропустить',
      skipTutorial: '跳过教程',
      intro: {
        step1: 'Капитан... Вы очнулись. Порча... она пожрала всё.',
        step2: 'Три дня назад культисты открыли Врата Бездны.\nЧёрная слизь мутировала наших братьев — людей в орков,\nгномов в троллей, эльфов в пауков.',
        step3: 'Теперь они ломятся в наш последний оплот на равнинах.\nМы — всё, что осталось от королевства Эридан.',
        step4: 'Ты — Капитан Выживших. Твоя задача:\n• Строить базу — казармы, кузницу, храм\n• Нанимать и вооружать бойцов\n• Отбивать ночные нападения\n• Проводить рейды, отвоёвывая землю',
        step5: 'Каждый день Порча усиливается.\nЗаполни Очки Освобождения — разблокируй союзные биомы:\nлеса эльфов, горы гномов, болота дварфов.',
        step6: 'Если падёт наш оплот — конец. Закрой Врата Бездны... или умри вместе с нами.',
      },
      base: {
        barracks: { title: 'Казарма', description: 'Казарма нужна для набора новобранцев, так же здесь располагаются все наши войска.' },
        raid: { title: 'Ворота', description: 'Ворота, через них отправляются отряды наших доблестных воинов вглубь территории для освобождения наших земель от порождений скверны.' },
        forge: { title: 'Кузница', description: 'Кузница, здесь создаётся оружие и броня для наших воинов.' },
        temple: { title: 'Храм', description: 'Храм, здесь с божьего благословения наши воины могут излечить свои раны.' },
        mageTower: { title: 'Башня мага', description: 'Башня мага, здесь маги варят зелья жизни и выносливости для поддержания наших воинов.' },
        castle: { title: 'Замок', description: 'Замок, здесь пишется летопись этой эпохи.' },
        taxHouse: { title: 'Дом старосты', description: 'Дом старосты, здесь собираются налоги с окрестных деревень. Налоги можно собирать раз в 15 минут, размер налога зависит от процента освобождения территории.' },
        orcAttack: { title: 'Нападение орков', description: 'Каждый час на базу нападают орки. Количество и состав врагов зависят от процента освобождения - чем выше освобождение, тем сильнее и больше врагов. При проигрыше в нападении теряется 20% очков освобождения.' },
        all: { title: 'В путь!', description: 'Найми первых новобранцев и отправляйся в свой первый рейд, чтобы освободить королевство от захватчиков.' },
      },
      extended: {
        resources: {
          liberation: { title: 'Очки освобождения', description: 'Это процент освобождённой территории. Когда достигнете 100% - завершится демо-версия. В полной версии вас ждут цитадели Эльфов, Гномов, Номадов и Ящеролюдей!' },
          gold: { title: 'Золото', description: 'Основная валюта. Нужно для найма бойцов, постройки зданий и покупки оружия.' },
          metal: { title: 'Металл', description: 'Нужен для создания оружия и брони в кузнице.' },
          wood: { title: 'Дерево', description: 'Нужно для создания щитов и некоторых видов оружия в кузнице.' },
          leather: { title: 'Кожа', description: 'Нужна для создания доспехов в кузнице.' },
          crystals: { title: 'Кристаллы', description: 'Редкий ресурс. Нужны для покупки зелий в башне мага.' },
          fighters: { title: 'Бойцы', description: 'Количество ваших воинов. Формат: текущее/максимум. Максимум можно увеличить через постройки.' },
          potions: { title: 'Зелья', description: 'Зелёное зелье - медленное лечение между боями. Красное зелье - быстрое лечение в бою.' },
        },
        taxHouse: {
          icon: { title: 'Дом старосты', description: 'Нажмите на иконку дома, чтобы открыть окно сбора налогов.' },
          collect: { title: 'Собрать налоги', description: 'Нажмите кнопку "Собрать налоги", чтобы получить золото. Налоги можно собирать раз в 15 минут.' },
        },
        barracks: {
          icon: { title: 'Казарма', description: 'Нажмите на иконку казармы, чтобы открыть окно управления войсками.' },
          build: { title: 'Построить казарму', description: 'Сначала нужно построить казарму. Нажмите кнопку "Построить".' },
          recruit: { title: 'Нанять бойца', description: 'Теперь найдите бойца и нажмите кнопку "Нанять".' },
          equip: { title: 'Экипировать бойца', description: 'Выберите оружие и щит для бойца, затем нажмите "Экипировать".' },
        },
        raid: {
          icon: { title: 'Ворота', description: 'Нажмите на иконку ворот, чтобы открыть окно выбора рейда.' },
          select: { title: 'Выбрать рейд', description: 'Выберите глубину рейда и бойцов для участия.' },
          start: { title: 'Начать рейд', description: 'Нажмите кнопку "Начать рейд", чтобы отправиться в бой.' },
        },
        battle: {
          unit: { title: 'Информация о юните', description: 'Здесь отображается имя, стамина и здоровье текущего юнита.' },
          movement: { title: 'Движение', description: 'Кликните на клетку, чтобы переместить юнита. Синие клетки - доступны для движения.' },
          obstacles: { title: 'Препятствия', description: 'Дерево - нельзя пройти и стрелять. Каряга - нельзя пройти, но можно стрелять. Куст - можно пройти, но тратится 2 стамины.' },
          attack: { title: 'Атака', description: 'Кликните на врага, чтобы атаковать. Красные клетки - враги в зоне атаки.' },
          defense: { title: 'Защита', description: 'Нажмите кнопку "ЩИТ", чтобы активировать защиту. Это уменьшит получаемый урон.' },
          turn: { title: 'Ход', description: 'Нажмите кнопку "ХОД", чтобы завершить ход текущего юнита.' },
          queue: { title: 'Очередь', description: 'Здесь отображается очередь ходов всех юнитов.' },
          end: { title: 'Завершение', description: 'Поздравляем! Вы завершили обучение. Теперь вы готовы к бою!' },
        },
      },
    },
  },
};

export const getTranslation = (lang: Language): Translations & {
  getWeaponName: (weaponName: string) => string;
  getFighterRankName: (rank: number) => string;
  getEnemyName: (enemyName: string) => string;
} => {
  const t = translations[lang] || translations.ru;
  const fallback = translations.ru;

  // Deep merge modals to ensure all nested properties are available
  const mergedModals = {
    ...fallback.modals,
    ...(t.modals || {}),
    forge: {
      ...fallback.modals.forge,
      ...(t.modals?.forge || {}),
    },
    barracks: {
      ...fallback.modals.barracks,
      ...(t.modals?.barracks || {}),
    },
    raid: {
      ...fallback.modals.raid,
      ...(t.modals?.raid || {}),
    },
    temple: {
      ...fallback.modals.temple,
      ...(t.modals?.temple || {}),
    },
    mageTower: {
      ...fallback.modals.mageTower,
      ...(t.modals?.mageTower || {}),
    },
    castle: {
      ...fallback.modals.castle,
      ...(t.modals?.castle || {}),
    },
    taxHouse: {
      ...fallback.modals.taxHouse,
      ...(t.modals?.taxHouse || {}),
    },
    orcAttack: {
      ...fallback.modals.orcAttack,
      ...(t.modals?.orcAttack || {}),
    },
  };

  return {
    ...t,
    modals: mergedModals,
    weapons: t.weapons || fallback.weapons,
    fighters: t.fighters || fallback.fighters,
    enemies: t.enemies || fallback.enemies,
    getWeaponName: (weaponName: string) => {
      const weaponKey = weaponName as keyof Translations['weapons'];
      return t.weapons?.[weaponKey] || fallback.weapons[weaponKey] || weaponName;
    },
    getFighterRankName: (rank: number) => {
      const rankKey = rank === 0 ? 'recruit' : rank === 1 ? 'fighter' : 'veteran';
      return t.fighters?.[rankKey as keyof Translations['fighters']] || fallback.fighters[rankKey as keyof Translations['fighters']] || 'Unknown';
    },
    getEnemyName: (enemyName: string) => {
      // Маппинг русских названий на ключи переводов
      const enemyMap: Record<string, keyof Translations['enemies']> = {
        'Костерез': 'bone_cutter',
        'Костяной убийца': 'bone_killer',
        'Топор-щитовик': 'axe_shield',
        'Мутант-кинжальщик': 'mutant_dagger',
        'Топорщик': 'axe_wielder',
        'Костяной топорщик': 'bone_axe_wielder',
        'Метатель': 'thrower',
        'Каменный разрушитель': 'stone_destroyer',
        'Копейщик': 'spearman',
      };
      const enemyKey = enemyMap[enemyName];
      if (enemyKey) {
        return t.enemies?.[enemyKey] || fallback.enemies[enemyKey] || enemyName;
      }
      return enemyName;
    },
  };
};

export const languageNames: Record<Language, string> = {
  en: 'English',
  ru: 'Русский',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  zh: '中文',
};

// Функция для получения локализованного названия оружия
export const getWeaponName = (weaponName: string, language: Language = 'ru'): string => {
  const weaponKey = weaponName as keyof Translations['weapons'];
  return translations[language]?.weapons?.[weaponKey] || translations.ru.weapons[weaponKey] || weaponName;
};

// Функция для получения локализованного названия ранга бойца
export const getFighterRankName = (rank: number, language: Language = 'ru'): string => {
  const rankKey = rank === 0 ? 'recruit' : rank === 1 ? 'fighter' : 'veteran';
  return translations[language]?.fighters?.[rankKey as keyof Translations['fighters']] || translations.ru.fighters[rankKey as keyof Translations['fighters']] || 'Unknown';
};

// Функция для получения локализованного названия противника
export const getEnemyName = (enemyName: string, language: Language = 'ru'): string => {
  // Маппинг русских названий на ключи переводов
  const enemyMap: Record<string, keyof Translations['enemies']> = {
    'Костерез': 'bone_cutter',
    'Костяной убийца': 'bone_killer',
    'Топор-щитовик': 'axe_shield',
    'Мутант-кинжальщик': 'mutant_dagger',
    'Топорщик': 'axe_wielder',
    'Костяной топорщик': 'bone_axe_wielder',
    'Метатель': 'thrower',
    'Каменный разрушитель': 'stone_destroyer',
    'Копейщик': 'spearman',
  };
  const enemyKey = enemyMap[enemyName];
  if (enemyKey) {
    return translations[language]?.enemies?.[enemyKey] || translations.ru.enemies[enemyKey] || enemyName;
  }
  return enemyName;
};

export default getTranslation;
